import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { driver, DriveStep, Driver } from 'driver.js';
import { BehaviorSubject, Observable } from 'rxjs';
import { UtilService } from '@app-shared/services/util.service';
import { ApiService } from '@app-shared/services/api.service';

export interface TourStepItem {
  id: string;
  title: string;
  description: string;
  route: string;
  element?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  badge?: string;
}

export interface TourModule {
  id: string;
  title: string;
  icon: string;
  description: string;
  tag: string;
  steps: TourStepItem[];
}

@Injectable({
  providedIn: 'root'
})
export class TourService {
  private driverObj: Driver | null = null;
  private isTourActive: boolean = false;
  private currentStepIndex: number = 0;

  // Guide Drawer Reactive State
  private _isGuideDrawerOpen = new BehaviorSubject<boolean>(false);
  public isGuideDrawerOpen$: Observable<boolean> = this._isGuideDrawerOpen.asObservable();

  private _selectedModuleId = new BehaviorSubject<string | null>(null);
  public selectedModuleId$: Observable<string | null> = this._selectedModuleId.asObservable();

  private _completedStepIds = new BehaviorSubject<Set<string>>(new Set());
  public completedStepIds$: Observable<Set<string>> = this._completedStepIds.asObservable();

  private _activeTourStep = new BehaviorSubject<{ moduleId: string; stepId: string } | null>(null);
  public activeTourStep$: Observable<{ moduleId: string; stepId: string } | null> = this._activeTourStep.asObservable();

  private readonly STORAGE_KEY = 'certiplate_tour_completed_steps';

  // Role Detection Getters
  public get isSystemSuperAdmin(): boolean {
    try {
      const isSSA = localStorage.getItem('isSystemSuperAdmin');
      if (isSSA === 'true' || isSSA === true as any) return true;
      const ur = this.util ? this.util.decrypt(localStorage.getItem('ur') || '') : '';
      if (ur === 'SA') return true;
      const useremail = (localStorage.getItem('useremail') || '').toLowerCase();
      if (useremail === 'admin@navriti.com') return true;
      return false;
    } catch (e) {
      return false;
    }
  }

  public get isAdminUser(): boolean {
    try {
      if (this.isSystemSuperAdmin) return true;
      const ur = this.util ? this.util.decrypt(localStorage.getItem('ur') || '') : '';
      const role = (localStorage.getItem('role') || '').toLowerCase();
      const email = (localStorage.getItem('useremail') || '').toLowerCase();
      const isOrgSuperAdmin = localStorage.getItem('isOrgSuperAdmin') === 'true';
      const isAdmin = localStorage.getItem('isAdmin') === 'true';
      return ur === 'SA' || ur === 'OA' || isOrgSuperAdmin || isAdmin || role.includes('admin') || email.includes('admin');
    } catch (e) {
      return false;
    }
  }

  // Feature / Module Segregated Tour Architecture - Exhaustive Field-Level Walkthroughs
  public get modules(): TourModule[] {
    return [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: 'compass',
      description: 'Explore dashboard essentials, metrics, and core navigation.',
      tag: 'Core',
      steps: [
        {
          id: 'gs-welcome',
          title: 'Welcome to Certiplate',
          description: 'Discover how Certiplate helps you create, manage, and conduct secure digital assessments.',
          route: '/Dashboard',
          badge: 'Overview'
        },
        {
          id: 'gs-dashboard',
          title: 'Dashboard Overview',
          description: 'View real-time statistics, active examinations, and overall organizational performance.',
          route: '/Dashboard',
          element: '[data-tour="dashboard-title"]',
          side: 'bottom',
          align: 'start',
          badge: 'Dashboard'
        },
        {
          id: 'gs-summary',
          title: 'Summary & Metrics',
          description: 'Analyze summary widgets including total questions, candidates, and certificates issued.',
          route: '/Dashboard',
          element: '[data-tour="summary-section"]',
          side: 'bottom',
          align: 'center',
          badge: 'Metrics'
        },
        {
          id: 'gs-date-filter',
          title: 'Date & Range Filter',
          description: 'Easily filter dashboard metrics by custom date intervals or pre-configured date ranges.',
          route: '/Dashboard',
          element: '[data-tour="date-filter"]',
          side: 'bottom',
          align: 'end',
          badge: 'Filters'
        },
        {
          id: 'gs-sidebar',
          title: 'Navigation Sidebar',
          description: 'Access all primary workspaces, question banks, candidate portals, and system settings.',
          route: '/Dashboard',
          element: '[data-tour="sidebar"]',
          side: 'right',
          align: 'start',
          badge: 'Navigation'
        }
      ]
    },
    {
      id: 'assessments',
      title: 'Assessments',
      icon: 'clipboard-check',
      description: 'Step-by-step creation, rules, candidate import, and publishing workflow.',
      tag: 'Operations',
      steps: [
        {
          id: 'asm-overview',
          title: 'Assessments Directory',
          description: 'Review and manage all scheduled, in-progress, and completed assessment plans.',
          route: '/Assessments/AssessmentPlan',
          element: '[data-tour="assessments-table"]',
          side: 'top',
          align: 'center',
          badge: 'Step 1: Directory'
        },
        {
          id: 'asm-add-btn',
          title: 'Add Assessment Button',
          description: 'Click the "+" button in the top right to launch the multi-step assessment plan wizard.',
          route: '/Assessments/AssessmentPlan',
          element: '[data-tour="add-assessment-btn"]',
          side: 'bottom',
          align: 'end',
          badge: 'Step 2: Action'
        },
        {
          id: 'asm-course',
          title: 'Course / Job Role',
          description: 'Choose the relevant qualification course or job role tied to the question repository.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-course-select"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 3: Course'
        },
        {
          id: 'asm-version',
          title: 'Course Syllabus Version',
          description: 'Select the approved course curriculum version governing this assessment.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-version-select"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 4: Version'
        },
        {
          id: 'asm-title',
          title: 'Assessment Title',
          description: 'Enter a unique and descriptive assessment title (e.g., Summative-Batch-2026-A).',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-title-input"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 5: Title'
        },
        {
          id: 'asm-project',
          title: 'Project Assignment',
          description: 'Link the assessment to an enterprise project code or create a new project on the fly.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-project-select"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 6: Project'
        },
        {
          id: 'asm-description',
          title: 'Assessment Description',
          description: 'Provide an overview of the test context, objectives, and batch qualifications using the rich editor.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-description-editor"]',
          side: 'top',
          align: 'center',
          badge: 'Step 7: Description'
        },
        {
          id: 'asm-location',
          title: 'Location & PIN Code Requirement',
          description: 'Enforce geo-location attendance by enabling address and postal PIN verification.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-location-section"]',
          side: 'top',
          align: 'start',
          badge: 'Step 8: Geography'
        },
        {
          id: 'asm-start-time',
          title: 'Assessment Start Date & Time',
          description: 'Pick the scheduled test start date and exact clock time via the calendar picker.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-start-time"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 9: Schedule'
        },
        {
          id: 'asm-end-time',
          title: 'Assessment End Date & Time',
          description: 'Configure the closing date and cutoff timestamp for candidate submissions.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-end-time"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 10: Schedule'
        },
        {
          id: 'asm-instructions',
          title: 'Candidate Instructions',
          description: 'Outline examination instructions, prohibited materials, and grading criteria shown before test start.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-instructions-editor"]',
          side: 'top',
          align: 'center',
          badge: 'Step 11: Instructions'
        },
        {
          id: 'asm-tags',
          title: 'Tag Type & Custom Values',
          description: 'Assign customer or organizational tags to filter and categorize assessment records.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-tag-type"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 12: Tags'
        },
        {
          id: 'asm-custom-attr',
          title: 'Custom Attributes Builder',
          description: 'Add dynamic custom metadata fields (text, date, email, file uploads) to capture custom session data.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-custom-attributes"]',
          side: 'top',
          align: 'center',
          badge: 'Step 13: Custom Fields'
        },
        {
          id: 'asm-save-step1',
          title: 'Save Assessment Details',
          description: 'Click Save to persist general parameters and proceed to Assessment Rules.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-save-step1-btn"]',
          side: 'top',
          align: 'center',
          badge: 'Step 14: Save Details'
        },
        {
          id: 'asm-rules-stakeholders',
          title: 'Stakeholder User Category',
          description: 'Select the stakeholder role category (Invigilator, Evaluator, Proctor, Administrator).',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-rules-stakeholders"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 15: Stakeholder Role'
        },
        {
          id: 'asm-rules-stakeholders-select',
          title: 'Select Specific Stakeholders',
          description: 'Choose invigilators and supervisors from the user dropdown tree to assign them to this session.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-rules-stakeholders-select"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 16: Assign Users'
        },
        {
          id: 'asm-rules-languages-specifier',
          title: 'Language(s) Specifier',
          description: 'Set language availability to "All available" or "Fixed" to restrict test languages.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-rules-languages-specifier"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 17: Languages'
        },
        {
          id: 'asm-rules-setscount',
          title: 'Count of Question Sets',
          description: 'Use the plus/minus stepper to choose how many randomized test sets (Set A, Set B, etc.) to generate.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-rules-setscount"]',
          side: 'bottom',
          align: 'center',
          badge: 'Step 18: Sets Count'
        },
        {
          id: 'asm-rules-primary',
          title: 'System Diagnostics & Responses',
          description: 'Enable candidate system checks (Webcam, Browser, Network) and post-submission response viewing.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-rules-primary"]',
          side: 'top',
          align: 'center',
          badge: 'Step 19: System Checks'
        },
        {
          id: 'asm-rules-loginmode',
          title: 'Login Authentication Mode',
          description: 'Select Access URL with Access Code, or Login ID credentials for candidate test entry.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-rules-loginmode"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 20: Login Mode'
        },
        {
          id: 'asm-rules-notification',
          title: 'Access Notification Channel',
          description: 'Choose whether candidates receive their credentials and test links via Email, SMS, or Both.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-rules-notification"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 21: Notification'
        },
        {
          id: 'asm-rules-deliverymode',
          title: 'Delivery Mode',
          description: 'Set delivery format: Online (remote), Offline (local test centre), or Mixed.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-rules-deliverymode"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 22: Delivery Mode'
        },
        {
          id: 'asm-rules-deliverycontrols',
          title: 'Delivery & Enrollment Controls',
          description: 'Configure self-enrollment, candidate registration approval, and test window access controls.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-rules-deliverycontrols"]',
          side: 'top',
          align: 'center',
          badge: 'Step 23: Enrollment'
        },
        {
          id: 'asm-rules-displayrules',
          title: 'Display & Question Shuffling',
          description: 'Configure question shuffling, answer option randomization, section grouping, and section multi-timers.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-rules-displayrules"]',
          side: 'top',
          align: 'center',
          badge: 'Step 24: Display Rules'
        },
        {
          id: 'asm-rules-questiondisplay',
          title: 'Questions Screen Layout',
          description: 'Choose question presentation layout: "All in one screen", "One per screen", or "Both with toggle".',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-rules-questiondisplay"]',
          side: 'top',
          align: 'start',
          badge: 'Step 25: Layout'
        },
        {
          id: 'asm-rules-monitoring',
          title: 'Monitoring Checkpoints',
          description: 'Enable candidate image snapshots and set capture interval frequency in seconds (minimum 15s).',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-rules-monitoring"]',
          side: 'top',
          align: 'center',
          badge: 'Step 26: Monitoring'
        },
        {
          id: 'asm-rules-proctoring',
          title: 'Advance AI Proctoring (Anti-Cheating)',
          description: 'Set face detection, spoof detection, tab-switching limits, head pose tracking, and real-time warnings.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-rules-proctoring"]',
          side: 'top',
          align: 'center',
          badge: 'Step 27: AI Proctoring'
        },
        {
          id: 'asm-rules-evidence',
          title: 'Evidence Controls',
          description: 'Require candidates to upload ID card photos, submit digital signatures, or record live audio/video.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-rules-evidence"]',
          side: 'top',
          align: 'center',
          badge: 'Step 28: Evidence'
        },
        {
          id: 'asm-save-rules',
          title: 'Save Assessment Rules',
          description: 'Save all assessment rules to persist parameters. Next, we will navigate to Candidate Roster and Publishing.',
          route: '/Assessments/AssessmentPlan/AddAssessmentPlan',
          element: '[data-tour="asm-save-rules-btn"]',
          side: 'top',
          align: 'center',
          badge: 'Step 29: Save Rules'
        },
        {
          id: 'asm-action-candidates',
          title: 'Row Action: Candidate Management',
          description: 'In the Assessments list table, open the row action menu and click "Candidates" to manage enrollment.',
          route: '/Assessments/AssessmentPlan',
          element: '[data-tour="assessments-table"]',
          side: 'top',
          align: 'center',
          badge: 'Step 30: Candidates Action'
        },
        {
          id: 'asm-manage-candidates',
          title: 'Candidate Roster Management',
          description: 'View the roster of enrolled candidates, access codes, and individual participation statuses.',
          route: '/Assessments/AssessmentPlan',
          element: '[data-tour="asm-manage-candidate-modal"]',
          side: 'top',
          align: 'center',
          badge: 'Step 31: Roster'
        },
        {
          id: 'asm-import-candidates',
          title: 'Import Pre-Registered Candidates',
          description: 'Click "Import Candidate" inside the modal to select and batch-import pre-registered candidate pools.',
          route: '/Assessments/AssessmentPlan',
          element: '[data-tour="asm-import-candidate-modal"]',
          side: 'top',
          align: 'center',
          badge: 'Step 32: Import'
        },
        {
          id: 'asm-add-candidate',
          title: 'Direct Candidate Registration',
          description: 'Click "Add Candidate" to manually register new candidates with name, email, phone, and registration ID.',
          route: '/Assessments/AssessmentPlan',
          element: '[data-tour="asm-add-candidate-modal"]',
          side: 'top',
          align: 'center',
          badge: 'Step 33: Direct Add'
        },
        {
          id: 'asm-publish-btn',
          title: 'Publish Assessment Button',
          description: 'Once candidates are imported and questions are verified, click "Publish Assessment" in the modal footer.',
          route: '/Assessments/AssessmentPlan',
          element: '[data-tour="asm-publish-btn"]',
          side: 'top',
          align: 'center',
          badge: 'Step 34: Publish'
        },
        {
          id: 'asm-confirm-publish',
          title: 'Confirm & Make Assessment Live',
          description: 'Confirm publishing in the dialog. Access credentials and secure examination URLs will be generated.',
          route: '/Assessments/AssessmentPlan',
          element: '[data-tour="asm-confirm-publish-modal"]',
          side: 'top',
          align: 'center',
          badge: 'Step 35: Live Test'
        }
      ]
    },
    {
      id: 'question-bank',
      title: 'Question Bank',
      icon: 'layer-group',
      description: 'Step-by-step authoring, multi-language editing, taxonomy, and indexing.',
      tag: 'Content',
      steps: [
        {
          id: 'qb-overview',
          title: 'Question Library Overview',
          description: 'Browse, filter, and organize questions categorized by course, version, label, and difficulty.',
          route: '/Library/Questions',
          element: '[data-tour="questions-list-table"], custom-table, .table-wrapper',
          side: 'top',
          align: 'center',
          badge: 'Step 1: Library'
        },
        {
          id: 'qb-add-btn',
          title: 'Add New Question Button',
          description: 'Click the "+" button in the top right to open the comprehensive question authoring workspace.',
          route: '/Library/Questions',
          element: '[data-tour="add-question-btn"]',
          side: 'bottom',
          align: 'end',
          badge: 'Step 2: Action'
        },
        {
          id: 'qb-course',
          title: 'Course / Job Role Selection',
          description: 'Select the course or job qualification this question belongs to, or create a new course.',
          route: '/Library/Questions/AddQuestion',
          element: '[data-tour="q-course-select"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 3: Course'
        },
        {
          id: 'qb-version',
          title: 'Course Syllabus Version',
          description: 'Choose the specific curriculum version or syllabus edition associated with the question.',
          route: '/Library/Questions/AddQuestion',
          element: '[data-tour="q-version-select"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 4: Version'
        },
        {
          id: 'qb-labels',
          title: 'Hierarchical Labels & Topics',
          description: 'Assign multi-level topic labels (Modules, Units, Learning Outcomes) for granular blueprint mapping.',
          route: '/Library/Questions/AddQuestion',
          element: '[data-tour="q-labels-select"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 5: Labels'
        },
        {
          id: 'qb-tags',
          title: 'Question Tags (Key / Value)',
          description: 'Attach custom key-value pairs (e.g., Bloom Taxonomy: Analytical, Year: 2026) for metadata search.',
          route: '/Library/Questions/AddQuestion',
          element: '[data-tour="q-tags-select"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 6: Tags'
        },
        {
          id: 'qb-eval-mode',
          title: 'Evaluation Mode',
          description: 'Choose between Automated grading, Manual assessor evaluation, or Hybrid assessment.',
          route: '/Library/Questions/AddQuestion',
          element: '[data-tour="q-evaluation-mode-select"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 7: Evaluation'
        },
        {
          id: 'qb-type',
          title: 'Question Type Selection',
          description: 'Select Question Type: Multiple Choice (MCQ), Multiple Select (MSQ), True/False, Fill in Blanks, Match, or Subjective.',
          route: '/Library/Questions/AddQuestion',
          element: '[data-tour="q-type-select"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 8: Type'
        },
        {
          id: 'qb-difficulty',
          title: 'Difficulty Level',
          description: 'Assign difficulty rating: Easy, Medium, or Hard to balance randomized question papers.',
          route: '/Library/Questions/AddQuestion',
          element: '[data-tour="q-difficulty-select"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 9: Difficulty'
        },
        {
          id: 'qb-editor',
          title: 'Question Text & Multi-Language Editor',
          description: 'Compose the main question prompt with rich text, formulas, symbols, and multi-language translations.',
          route: '/Library/Questions/AddQuestion',
          element: '[data-tour="q-text-editor"]',
          side: 'top',
          align: 'center',
          badge: 'Step 10: Editor'
        },
        {
          id: 'qb-attachments',
          title: 'Media & Document Attachments',
          description: 'Upload supporting images, audio clips, diagrams, or reference PDF files for candidates.',
          route: '/Library/Questions/AddQuestion',
          element: '[data-tour="q-attachment-upload"]',
          side: 'top',
          align: 'center',
          badge: 'Step 11: Attachments'
        },
        {
          id: 'qb-options',
          title: 'Answer Options & Correct Keys',
          description: 'Enter answer choices, mark correct options/keys, configure matching columns, or set subjective grading rubrics.',
          route: '/Library/Questions/AddQuestion',
          element: '.option, [data-tour="q-options-container"], .trueFalseOptions, .rdioLabelDiv, mat-radio-group[formControlName="isCorrectAnswer"], app-addoptiondata, app-fillintheblanks, app-matchthefollowings',
          side: 'top',
          align: 'center',
          badge: 'Step 12: Options'
        },
        {
          id: 'qb-explanation',
          title: 'Answer Explanation & Remarks',
          description: 'Add detailed solution explanations, keywords, and assessor evaluation feedback for transparent scoring.',
          route: '/Library/Questions/AddQuestion',
          element: '[data-tour="q-explanation-field"], input[placeholder*="Answer Explanation"], input[formcontrolname*="answerExplanation"], .form-group:has(input[placeholder*="Answer Explanation"])',
          side: 'top',
          align: 'center',
          badge: 'Step 13: Solution'
        },
        {
          id: 'qb-save',
          title: 'Save & Index Question',
          description: 'Verify all metadata and click Save to store the question securely into the verified question bank.',
          route: '/Library/Questions/AddQuestion',
          element: 'button#submit.submitbtn, button#submit, [data-tour="q-save-btn"]',
          side: 'top',
          align: 'center',
          badge: 'Step 14: Save'
        }
      ]
    },
    {
      id: 'question-papers',
      title: 'Question Papers',
      icon: 'file-alt',
      description: 'Blueprint matrix creation, scoring rules, and randomized set compilation.',
      tag: 'Blueprint',
      steps: [
        {
          id: 'qp-overview',
          title: 'Question Papers Directory',
          description: 'View all question paper blueprints, active sets (Set A, Set B), and version histories.',
          route: '/Library/QuestionPapers',
          element: '[data-tour="qp-list-table"]',
          side: 'top',
          align: 'center',
          badge: 'Step 1: Library'
        },
        {
          id: 'qp-add-btn',
          title: 'Create Paper Blueprint Button',
          description: 'Click the "+" button in the top right to start building a new question paper version blueprint.',
          route: '/Library/QuestionPapers',
          element: '[data-tour="add-question-paper-btn"]',
          side: 'bottom',
          align: 'end',
          badge: 'Step 2: Action'
        },
        {
          id: 'qp-stepper',
          title: 'Generation Stepper Navigation',
          description: 'Navigate between Step 1 (Question Paper Blueprint) and Step 2 (Evaluation Criteria).',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-generation-stepper"]',
          side: 'bottom',
          align: 'center',
          badge: 'Step 3: Stepper'
        },
        {
          id: 'qp-course',
          title: 'Select Course / Job Role',
          description: 'Choose the syllabus or qualification program from which questions will be sampled.',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-course-select"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 4: Course'
        },
        {
          id: 'qp-version',
          title: 'Course Syllabus Version',
          description: 'Specify the course edition to ensure questions match current curriculum benchmarks.',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-version-select"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 5: Version'
        },
        {
          id: 'qp-labels',
          title: 'Topic & Blueprint Labels',
          description: 'Filter question sampling by selecting specific units, chapters, or syllabus modules.',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-labels-select"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 6: Labels'
        },
        {
          id: 'qp-tags',
          title: 'Question Paper Tags',
          description: 'Attach organizational classification tags to easily filter papers in the repository.',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-tags-select"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 7: Tags'
        },
        {
          id: 'qp-name',
          title: 'Question Paper Version Name',
          description: 'Enter a descriptive paper name (e.g., Final-Summative-Theory-2026).',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-name-input"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 8: Paper Name'
        },
        {
          id: 'qp-shortname',
          title: 'Paper Short Code / Alias',
          description: 'Auto-generated short code for quick indexing and candidate paper header identification.',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-shortname-input"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 9: Short Name'
        },
        {
          id: 'qp-time-limit',
          title: 'Examination Time Limit',
          description: 'Define total allowable exam time in minutes (e.g., 60, 90, 120 minutes).',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-time-limit"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 10: Time Limit'
        },
        {
          id: 'qp-question-count',
          title: 'Count of Questions (CQ)',
          description: 'Set total number of questions to be presented to each candidate in their paper set.',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-question-count"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 11: Question Count'
        },
        {
          id: 'qp-total-marks',
          title: 'Total Maximum Marks',
          description: 'Specify aggregate maximum score, ensuring it balances with marks assigned per question.',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-total-marks"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 12: Total Marks'
        },
        {
          id: 'qp-version-count',
          title: 'Count of Versions / Sets',
          description: 'Define how many distinct randomized sets (Set A, Set B, Set C) should be generated.',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-version-count"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 13: Sets Count'
        },
        {
          id: 'qp-overlap',
          title: 'Question Overlap Allowed Slider',
          description: 'Set maximum allowable duplicate questions across different sets (0 = completely disjoint papers).',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-overlap-slider"]',
          side: 'bottom',
          align: 'center',
          badge: 'Step 14: Overlap'
        },
        {
          id: 'qp-instructions',
          title: 'Examination Instructions',
          description: 'Format test-specific candidate guidelines, calculator rules, and negative marking warnings.',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-instructions-editor"]',
          side: 'top',
          align: 'center',
          badge: 'Step 15: Instructions'
        },
        {
          id: 'qp-marks-mapping',
          title: 'Assign Marks Matrix',
          description: 'Configure rules mapping Evaluation Mode, Topic Labels, and Difficulty to specific Marks weightage.',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-marks-mapping"]',
          side: 'top',
          align: 'center',
          badge: 'Step 16: Scoring Matrix'
        },
        {
          id: 'qp-grouping-criteria',
          title: 'Assign Grouping Criteria',
          description: 'Define section grouping rules (e.g., Section A: Objective, Section B: Practical Viva).',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-grouping-criteria"]',
          side: 'top',
          align: 'center',
          badge: 'Step 17: Sections'
        },
        {
          id: 'qp-save',
          title: 'Save & Continue Blueprint',
          description: 'Click "Save & Continue" to advance to Step 2 (Evaluation Criteria) for scoring and pass/fail rules.',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-save-btn"]',
          side: 'top',
          align: 'center',
          badge: 'Step 18: Save & Continue'
        },
        {
          id: 'qp-eval-title',
          title: 'Evaluation Criteria Title',
          description: 'Specify the evaluation scheme title/alias to label this scoring profile.',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-eval-title"], #alias',
          side: 'bottom',
          align: 'start',
          badge: 'Step 19: Evaluation Title'
        },
        {
          id: 'qp-eval-tag-type',
          title: 'Tag Type & Values',
          description: 'Categorize this evaluation criteria with tag types (e.g., Project, Batch, Custom tags).',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-eval-tag-type"], #ddlCriteria',
          side: 'bottom',
          align: 'start',
          badge: 'Step 20: Tag Type'
        },
        {
          id: 'qp-eval-description',
          title: 'Evaluation Scheme Description',
          description: 'Add instructions, evaluation guidelines, or candidate criteria notes in the rich text editor.',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-eval-description"], app-quill-editor[formcontrolname="description"]',
          side: 'top',
          align: 'center',
          badge: 'Step 21: Description'
        },
        {
          id: 'qp-eval-criteria-select',
          title: 'Evaluation Criteria Mode',
          description: 'Select parameter mode: Overall score, Section-wise passing, or Grouping criteria.',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-eval-criteria-select"], #ddlEvaluationCriteria',
          side: 'bottom',
          align: 'start',
          badge: 'Step 22: Criteria Mode'
        },
        {
          id: 'qp-eval-params-table',
          title: 'Evaluation Parameters & Passing Rules',
          description: 'Review configured evaluation parameters and define the minimum pass percentage (%) required.',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-eval-params-table"], .part3',
          side: 'top',
          align: 'center',
          badge: 'Step 23: Passing Rules'
        },
        {
          id: 'qp-eval-grading-table',
          title: 'Grading Criteria & Grade Brackets',
          description: 'Define score percentage bands (Min % to Max %) and map them to letter grades (e.g., A+, A, B, C) or classifications.',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-eval-grading-table"], .part4',
          side: 'top',
          align: 'center',
          badge: 'Step 24: Grade Brackets'
        },
        {
          id: 'qp-save-evaluation-btn',
          title: 'Save Evaluation Criteria',
          description: 'Click "Save Evaluation" to store the evaluation rules and finalize the complete blueprint.',
          route: '/Library/QuestionPapers/AddQuestionPapers',
          element: '[data-tour="qp-save-evaluation-btn"], button#submit.submitbtn, button#submit',
          side: 'top',
          align: 'center',
          badge: 'Step 25: Save Evaluation'
        },
        {
          id: 'qp-action-generate',
          title: 'Row Action: Generate Question Paper',
          description: 'In the Question Papers directory, click the row action menu and select "Generate Question Paper".',
          route: '/Library/QuestionPapers',
          element: '[data-tour="qp-list-table"]',
          side: 'top',
          align: 'center',
          badge: 'Step 26: Action'
        },
        {
          id: 'qp-generation-methods',
          title: 'Questions Selection Method',
          description: 'Choose Manual Selection (cherry-pick questions) or Automated Generation (AI rule-based sampling).',
          route: '/Library/QuestionPapers',
          element: '[data-tour="qp-modal-methods"]',
          side: 'top',
          align: 'center',
          badge: 'Step 27: Selection Mode'
        },
        {
          id: 'qpg-metrics',
          title: 'Target Blueprint Quotas',
          description: 'Track live marks allocation (M) and selected question count (Q) against blueprint targets in the top bar.',
          route: '/Library/QuestionPapers/QuestionPaperGeneration',
          element: '[data-tour="qpg-metrics-bar"]',
          side: 'bottom',
          align: 'center',
          badge: 'Step 28: Target Quotas'
        },
        {
          id: 'qpg-filter',
          title: 'Question Repository Filters',
          description: 'Open the filter panel to filter questions by type, difficulty level, taxonomy labels, and evaluation modes.',
          route: '/Library/QuestionPapers/QuestionPaperGeneration',
          element: '[data-tour="qpg-filter-btn"]',
          side: 'bottom',
          align: 'center',
          badge: 'Step 29: Filter Questions'
        },
        {
          id: 'qpg-select',
          title: 'Question Selection',
          description: 'Use checkboxes or "Select All" to pick and review questions matching your blueprint.',
          route: '/Library/QuestionPapers/QuestionPaperGeneration',
          element: '[data-tour="qpg-questions-container"]',
          side: 'top',
          align: 'center',
          badge: 'Step 30: Question Selection'
        },
        {
          id: 'qpg-preview',
          title: 'Preview Question Paper',
          description: 'Preview the formatted question paper with sections, groupings, and instructions before publishing.',
          route: '/Library/QuestionPapers/QuestionPaperGeneration',
          element: '[data-tour="qpg-preview-btn"]',
          side: 'bottom',
          align: 'center',
          badge: 'Step 31: Preview'
        },
        {
          id: 'qpg-publish',
          title: 'Publish Question Paper',
          description: 'Click "Publish Question Paper" to finalize and lock the paper version for live examination scheduling.',
          route: '/Library/QuestionPapers/QuestionPaperGeneration',
          element: '[data-tour="qpg-publish-btn"]',
          side: 'bottom',
          align: 'center',
          badge: 'Step 32: Publish'
        }
      ]
    },
    {
      id: 'candidates',
      title: 'Candidates',
      icon: 'users',
      description: 'Enroll candidates, manage batches, import/export rosters, and configure credentials.',
      tag: 'Enrollment',
      steps: [
        {
          id: 'cand-overview',
          title: 'Candidates Directory & Roster',
          description: 'Search, filter, and inspect registered candidates, regional assignments, TAT performance, and credentials status.',
          route: '/Assessments/Candidate',
          element: '[data-tour="candidates-table"], [data-tour="custom-table"], table',
          side: 'top',
          align: 'center',
          badge: 'Step 1: Directory'
        },
        {
          id: 'cand-search',
          title: 'Quick Search Bar',
          description: 'Search across candidate names, usernames, districts, states, pincodes, or custom attributes in real-time.',
          route: '/Assessments/Candidate',
          element: '[data-tour="candidates-search"], custom-table .all-text-filter, custom-table .search-wrapper, [data-tour="table-search"]',
          side: 'bottom',
          align: 'end',
          badge: 'Step 2: Search'
        },
        {
          id: 'cand-filter',
          title: 'Advanced Candidate Filters',
          description: 'Filter candidates by Added Date, Account Status (Active/Inactive/Archived), TAT Analysis, and Pass Percentage thresholds.',
          route: '/Assessments/Candidate',
          element: '[data-tour="candidates-filter-btn"], custom-table app-filter, custom-table .filterbtn, [data-tour="table-filter-btn"]',
          side: 'bottom',
          align: 'end',
          badge: 'Step 3: Filter'
        },
        {
          id: 'cand-download',
          title: 'Export / Download Candidates',
          description: 'Export the complete or filtered candidate roster to an Excel spreadsheet (.xlsx) for offline records and audits.',
          route: '/Assessments/Candidate',
          element: '[data-tour="cand-download-btn"], custom-table button[mattooltip*="Download"], custom-table .download, custom-table .fa-download',
          side: 'bottom',
          align: 'end',
          badge: 'Step 4: Download'
        },
        {
          id: 'cand-upload',
          title: 'Bulk Upload & Roster Import',
          description: 'Import multiple candidates in bulk by uploading Excel/CSV rosters with standard templates and automatic validation.',
          route: '/Assessments/Candidate',
          element: '[data-tour="cand-upload-btn"], custom-table button[mattooltip*="Upload"], custom-table .upload, custom-table .fa-upload',
          side: 'bottom',
          align: 'end',
          badge: 'Step 5: Upload'
        },
        {
          id: 'cand-add-btn',
          title: 'Single Candidate Registration',
          description: 'Click the "+" button to open the single-candidate enrollment and credential registration form.',
          route: '/Assessments/Candidate',
          element: '[data-tour="add-candidate-btn"], custom-table .action-button, custom-table .add, custom-table .fa-plus',
          side: 'bottom',
          align: 'end',
          badge: 'Step 6: Enroll'
        },
        {
          id: 'cand-name',
          title: 'Candidate Full Name',
          description: 'Enter the candidate full legal name as it should appear on official examination records and certificates.',
          route: '/Assessments/Candidate/AddCandidate',
          element: '[data-tour="cand-name-input"], #nameInAddCandidateFormId',
          side: 'bottom',
          align: 'start',
          badge: 'Step 7: Full Name'
        },
        {
          id: 'cand-pincode',
          title: 'Pincode & Geography Verification',
          description: 'Select pincode availability and enter a 6-digit postal code. Click Search to automatically detect state and district.',
          route: '/Assessments/Candidate/AddCandidate',
          element: '[data-tour="cand-pincode-group"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 8: Pincode'
        },
        {
          id: 'cand-geography',
          title: 'State & District Allocation',
          description: 'Review auto-loaded state and district or manually assign the candidate geographic examination jurisdiction.',
          route: '/Assessments/Candidate/AddCandidate',
          element: '[data-tour="cand-state-district"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 9: Geography'
        },
        {
          id: 'cand-username',
          title: 'Unique Username & Validation',
          description: 'Assign a unique candidate portal username. The system checks availability in real-time and provides smart suggestions.',
          route: '/Assessments/Candidate/AddCandidate',
          element: '[data-tour="cand-username-input"], #userNameInAddCandidateFormId',
          side: 'bottom',
          align: 'start',
          badge: 'Step 10: Username'
        },
        {
          id: 'cand-contact',
          title: 'Contact Credentials (Email & Phone)',
          description: 'Enter email address and mobile phone number for automated login credentials, exam alerts, and result delivery.',
          route: '/Assessments/Candidate/AddCandidate',
          element: '[data-tour="cand-contact-fields"]',
          side: 'bottom',
          align: 'start',
          badge: 'Step 11: Contact'
        },
        {
          id: 'cand-attributes',
          title: 'Custom Metadata Attributes',
          description: 'Attach dynamic organizational fields such as Employee ID, Batch Number, Department, or document attachments.',
          route: '/Assessments/Candidate/AddCandidate',
          element: '[data-tour="cand-custom-attributes"]',
          side: 'top',
          align: 'center',
          badge: 'Step 12: Attributes'
        },
        {
          id: 'cand-save',
          title: 'Save & Enroll Candidate',
          description: 'Save the candidate profile to instantly create their assessment record and ready their account for exam allocation.',
          route: '/Assessments/Candidate/AddCandidate',
          element: '[data-tour="cand-save-btn"], #submit',
          side: 'top',
          align: 'center',
          badge: 'Step 13: Save'
        },
        {
          id: 'cand-actions',
          title: 'Candidate Operations & Actions',
          description: 'Use the row action menu to Modify Profiles, Generate Login Credentials, view TAT & Pass Analytics, or issue Admit Cards.',
          route: '/Assessments/Candidate',
          element: '[data-tour="candidates-table"], [data-tour="custom-table"], table',
          side: 'top',
          align: 'center',
          badge: 'Step 14: Actions'
        }
      ]
    },
    {
      id: 'settings',
      title: this.isSystemSuperAdmin ? 'Settings & Administration' : 'Settings',
      icon: 'cog',
      description: this.isSystemSuperAdmin
        ? 'System Super Admin & Organization controls: multi-tenancy, subscriptions, developer APIs, RBAC, and legal compliance.'
        : 'Manage user access permissions, platform integrations, and notification preferences.',
      tag: this.isSystemSuperAdmin ? 'System Admin' : 'Configuration',
      steps: this.isSystemSuperAdmin
        ? [
            {
              id: 'set-trigger',
              title: 'Settings & System Administration Menu',
              description: 'Click the gear icon in the top header to access central multi-tenant management, subscription tiers, developer console, user IAM permissions, integration pipelines, and compliance documents.',
              route: '/admin/org-list',
              element: '[data-tour="header-settings"]',
              side: 'bottom',
              align: 'end',
              badge: 'Step 1: Settings Menu'
            },
            {
              id: 'set-org-settings-menu',
              title: 'Org Settings & Multi-Tenancy Overview',
              description: 'Access the root organization directory to manage client tenants, LDAP configurations, license quotas, MOUs, and institutional hierarchies.',
              route: '/admin/org-list',
              element: '[data-tour="set-org-settings"]',
              side: 'left',
              align: 'start',
              badge: 'Step 2: Org Settings Menu'
            },
            {
              id: 'set-org-directory',
              title: 'Organization Master Directory & Columns',
              description: 'View the complete registered organizations ledger: S.No., Organization Name, LDAP Enabled status, Industry Vertical, MOU File download/preview, Geographic Location (State & District), Active License key, and status toggle.',
              route: '/admin/org-list',
              element: '[data-tour="custom-table"], table',
              side: 'top',
              align: 'center',
              badge: 'Step 3: Org Directory'
            },
            {
              id: 'set-org-filters-search',
              title: 'Search & Multi-Attribute Org Filters',
              description: 'Instantly find organizations using keyword search, or filter by Industry Vertical (Corporate / University / Vocational), LDAP integration status, and geographic state.',
              route: '/admin/org-list',
              element: '[data-tour="organizations-search"], custom-table .all-text-filter, custom-table .search-wrapper, [data-tour="organizations-filter-btn"], [data-tour="table-search"]',
              side: 'bottom',
              align: 'end',
              badge: 'Step 4: Search & Filters'
            },
            {
              id: 'set-org-add-btn',
              title: 'Add Organization Action',
              description: 'Click the "+" button in the top action bar to launch the organization provisioning and onboarding wizard.',
              route: '/admin/org-list',
              element: 'custom-table .action-button, [data-tour="add-org-btn"], [data-tour="add-question-btn"], custom-table .add',
              side: 'bottom',
              align: 'end',
              badge: 'Step 5: Add Organization'
            },
            {
              id: 'set-org-form-identity',
              title: 'Organization Name, Vertical & Brand Logo',
              description: 'Enter the official legal entity Organization Name, select the Industry Vertical (e.g. Higher Education, Corporate, Healthcare), and upload the organization brand logo (up to 2MB, 1000x600px).',
              route: '/admin/add-org',
              element: 'form, .container',
              side: 'top',
              align: 'center',
              badge: 'Step 6: Organization Identity'
            },
            {
              id: 'set-org-form-geo',
              title: 'PIN Code Lookup, State & District Auto-Fill',
              description: 'Select PIN Code option, enter 6-digit postal code, and click the search icon to automatically fetch and populate State and District, then configure the registered Billing Address.',
              route: '/admin/add-org',
              element: 'form, .container',
              side: 'top',
              align: 'center',
              badge: 'Step 7: Address & Geography'
            },
            {
              id: 'set-org-license-modal',
              title: 'License Key & Candidate Quota Allocation',
              description: 'Configure tenant license credentials, validity timeframes, maximum concurrent active test-taker licenses, and storage allocations.',
              route: '/admin/org-list',
              element: '[data-tour="custom-table"], table',
              side: 'top',
              align: 'center',
              badge: 'Step 8: License Management'
            },
            {
              id: 'set-org-revision-history',
              title: 'Organization & MOU Revision Audit Timeline',
              description: 'Inspect vertical historical audit timelines recording all past changes to organization profiles, MOU effective date periods, and administrative ownership transfers.',
              route: '/admin/org-list',
              element: '[data-tour="custom-table"], table',
              side: 'top',
              align: 'center',
              badge: 'Step 9: Revision History'
            },
            {
              id: 'set-subscriptions-menu',
              title: 'Subscription Plans Menu Option',
              description: 'Navigate to the commercial subscription management dashboard to define tiers, feature packaging, and billing quotas.',
              route: '/admin/subscriptions',
              element: '[data-tour="set-subscriptions"]',
              side: 'left',
              align: 'start',
              badge: 'Step 10: Subscriptions Menu'
            },
            {
              id: 'set-sub-directory',
              title: 'Subscription Plans Catalog & Entitlements',
              description: 'Review active subscription plans catalog displaying Plan Name, Price, Candidate Volume limits, Subscribed Feature checklist, and Active/Inactive operational status.',
              route: '/admin/subscriptions',
              element: '[data-tour="custom-table"], table',
              side: 'top',
              align: 'center',
              badge: 'Step 11: Plan Directory'
            },
            {
              id: 'set-sub-add-edit',
              title: 'Configure Features & Plan Entitlements',
              description: 'Click the 3-dots actions menu on any subscription plan row and select "Configure features" to customize enabled platform modules, proctoring controls, and entitlements, then click "Confirm".',
              route: '/admin/subscriptions',
              element: '#configureFeaturesModal .modal-content, #configureFeaturesModal, custom-table tr:first-child button[aria-label="menu icon"], .action-grid-item.configureFeatures',
              side: 'top',
              align: 'center',
              badge: 'Step 12: Configure Features'
            },
            {
              id: 'set-dev-console-menu',
              title: 'Developer Console Menu Option',
              description: 'Open the Developer Console hub to manage programmatic REST API access tokens, rate limits, and audit logs.',
              route: '/dev-console',
              element: '[data-tour="set-dev-console"]',
              side: 'left',
              align: 'start',
              badge: 'Step 13: Dev Console Menu'
            },
            {
              id: 'set-dev-keys-cards',
              title: 'API Keys Dashboard & Usage Limits',
              description: 'View active API key cards displaying Key ID, masked Secret Token with instant one-click clipboard copy, Monthly Request Limit, Expiration Date, and instantaneous Active toggle.',
              route: '/dev-console',
              element: '[data-tour="dev-api-card"], .card, [data-tour="dev-no-keys"], .no-keys, [data-tour="dev-keys-container"], .cards-container',
              side: 'top',
              align: 'center',
              badge: 'Step 14: API Keys'
            },
            {
              id: 'set-dev-generate-key',
              title: 'Generate API Key & Role Access Scopes',
              description: 'Provision new API credentials: set Monthly Request Usage Limits, choose Expiration Date, and restrict endpoint operations using fine-grained Role Access modal selections.',
              route: '/generate-key',
              element: 'form, .form-container, .container',
              side: 'top',
              align: 'center',
              badge: 'Step 15: Generate Key'
            },
            {
              id: 'set-dev-logs',
              title: 'API Request Audit Logs & Latency Tracking',
              description: 'Click View Logs on any key to monitor real-time HTTP requests, response status codes (200 OK, 401 Unauthorized, 500 Error), caller IP addresses, and response latency.',
              route: '/dev-console',
              element: '[data-tour="dev-view-logs"], .card-row.view-logs, [data-tour="dev-api-card"], .card, [data-tour="dev-no-keys"], .no-keys, [data-tour="dev-keys-container"], .cards-container',
              side: 'top',
              align: 'center',
              badge: 'Step 16: API Audit Logs'
            },
            {
              id: 'set-user-settings-menu',
              title: 'User Settings Overview',
              description: 'User Settings governs system access, staff hierarchies, and user accounts across Roles, User Categories, and Users.',
              route: '/UserSettings/Roles',
              element: '[data-tour="set-user-settings"]',
              side: 'left',
              align: 'start',
              badge: 'Step 17: User Settings'
            },
            {
              id: 'set-roles',
              title: 'Roles & Permissions (RBAC)',
              description: 'Define granular Role-Based Access Control permissions across Modules (Questions, Assessments, Operations, Reports) with Read, Create, Edit, Delete, and Publish privileges.',
              route: '/UserSettings/Roles',
              element: '[data-tour="roles-table"], [data-tour="custom-table"], table',
              side: 'top',
              align: 'center',
              badge: 'Step 18: Roles'
            },
            {
              id: 'set-user-categories',
              title: 'User Categories & Hierarchy',
              description: 'Group evaluators, proctors, invigilators, and staff into standardized categories. Configure supervisory reporting hierarchy requirements and custom attributes.',
              route: '/UserSettings/UserCategory',
              element: '[data-tour="user-category-table"], [data-tour="custom-table"], table',
              side: 'top',
              align: 'center',
              badge: 'Step 19: Categories'
            },
            {
              id: 'set-users',
              title: 'User Accounts & Credentials',
              description: 'Manage staff and administrator profiles. Enroll team members, assign system roles and categories, configure reporting managers, and issue login credentials.',
              route: '/UserSettings/Users',
              element: '[data-tour="users-table"], [data-tour="custom-table"], table',
              side: 'top',
              align: 'center',
              badge: 'Step 20: Users'
            },
            {
              id: 'set-integration-menu',
              title: 'Integration Overview',
              description: 'Integration connects Certiplate with institutional ERPs, LMS platforms, and student information systems via Data Mapping and API Integration.',
              route: '/data-mapping',
              element: '[data-tour="set-integration"]',
              side: 'left',
              align: 'center',
              badge: 'Step 21: Integrations'
            },
            {
              id: 'set-data-mapping',
              title: 'Data Mapping & Schema Translation',
              description: 'Create integration profiles that map external database/JSON fields to Certiplate internal schemas for candidates, courses, batches, and examination results.',
              route: '/data-mapping',
              element: '[data-tour="attribute-mapping-table"], [data-tour="custom-table"], table',
              side: 'top',
              align: 'center',
              badge: 'Step 22: Data Mapping'
            },
            {
              id: 'set-api-integration',
              title: 'API Integration & Developer Tools',
              description: 'Manage REST API keys, test endpoints with the interactive request builder (GET/POST/PUT/DELETE), import cURL requests, and configure event webhooks.',
              route: '/api-integration',
              element: '[data-tour="custom-table"], table',
              side: 'top',
              align: 'center',
              badge: 'Step 23: API Integration'
            },
            {
              id: 'set-notifications',
              title: 'Notification Settings & Multi-Channel Alerts',
              description: 'Configure automated notification matrices across Email, SMS, Push, and In-App channels for exam registrations, reminder alerts, and test score broadcasts.',
              route: '/settings/notification-settings',
              element: '[data-tour="notification-settings-grid"], .container',
              side: 'top',
              align: 'center',
              badge: 'Step 24: Notifications'
            },
            {
              id: 'set-compliance-menu',
              title: 'Compliance Pages Menu Option',
              description: 'Access the legal document hub to maintain institutional terms of service, candidate data privacy policies, and GDPR/CCPA cookie consents.',
              route: '/settings/compliance-pages',
              element: '[data-tour="set-compliance"]',
              side: 'left',
              align: 'start',
              badge: 'Step 25: Compliance Menu'
            },
            {
              id: 'set-compliance-dashboard',
              title: 'Legal Compliance & Policy Documents Hub',
              description: 'Manage master compliance policy documents for Terms and Conditions, Privacy Policy, and Cookie Policy with live status indicators and current version labels.',
              route: '/settings/compliance-pages',
              element: '.policy-cards-row, .compliance-wrapper',
              side: 'top',
              align: 'center',
              badge: 'Step 26: Compliance Hub'
            },
            {
              id: 'set-compliance-upload',
              title: 'Policy Revision Upload & Version Control',
              description: 'Drag and drop new policy document revisions (.docx format, up to 25MB), input revision changelog notes, track complete version history, and download previous policy versions.',
              route: '/settings/compliance-pages',
              element: '.dropzone-box, .upload-section, .compliance-wrapper',
              side: 'top',
              align: 'center',
              badge: 'Step 27: Policy Revision'
            }
          ]
        : [
            {
              id: 'set-trigger',
              title: 'Settings & Administration Menu',
              description: 'Click the gear icon in the top header to access user identity controls, enterprise integrations, and notification preferences.',
              route: '/UserSettings/Roles',
              element: '[data-tour="header-settings"]',
              side: 'bottom',
              align: 'end',
              badge: 'Step 1: Settings Menu'
            },
            {
              id: 'set-user-settings-menu',
              title: 'User Settings Overview',
              description: 'User Settings governs system access, staff hierarchies, and user accounts across Roles, User Categories, and Users.',
              route: '/UserSettings/Roles',
              element: '[data-tour="set-user-settings"]',
              side: 'left',
              align: 'start',
              badge: 'Step 2: User Settings'
            },
            {
              id: 'set-roles',
              title: 'Roles & Permissions (RBAC)',
              description: 'Define granular Role-Based Access Control permissions across Modules (Questions, Assessments, Operations, Reports) with Read, Create, Edit, Delete, and Publish privileges.',
              route: '/UserSettings/Roles',
              element: '[data-tour="roles-table"], [data-tour="custom-table"], table',
              side: 'top',
              align: 'center',
              badge: 'Step 3: Roles'
            },
            {
              id: 'set-user-categories',
              title: 'User Categories & Hierarchy',
              description: 'Group evaluators, proctors, invigilators, and staff into standardized categories. Configure supervisory reporting hierarchy requirements and custom attributes.',
              route: '/UserSettings/UserCategory',
              element: '[data-tour="user-category-table"], [data-tour="custom-table"], table',
              side: 'top',
              align: 'center',
              badge: 'Step 4: Categories'
            },
            {
              id: 'set-users',
              title: 'User Accounts & Credentials',
              description: 'Manage staff and administrator profiles. Enroll team members, assign system roles and categories, configure reporting managers, and issue login credentials.',
              route: '/UserSettings/Users',
              element: '[data-tour="users-table"], [data-tour="custom-table"], table',
              side: 'top',
              align: 'center',
              badge: 'Step 5: Users'
            },
            {
              id: 'set-integration-menu',
              title: 'Integration Overview',
              description: 'Integration connects Certiplate with institutional ERPs, LMS platforms, and student information systems via Data Mapping and API Integration.',
              route: '/data-mapping',
              element: '[data-tour="set-integration"]',
              side: 'left',
              align: 'center',
              badge: 'Step 6: Integrations'
            },
            {
              id: 'set-data-mapping',
              title: 'Data Mapping & Schema Translation',
              description: 'Create integration profiles that map external database/JSON fields to Certiplate internal schemas for candidates, courses, batches, and examination results.',
              route: '/data-mapping',
              element: '[data-tour="attribute-mapping-table"], [data-tour="custom-table"], table',
              side: 'top',
              align: 'center',
              badge: 'Step 7: Data Mapping'
            },
            {
              id: 'set-api-integration',
              title: 'API Integration & Developer Tools',
              description: 'Manage REST API keys, test endpoints with the interactive request builder (GET/POST/PUT/DELETE), import cURL requests, and configure event webhooks.',
              route: '/api-integration',
              element: '[data-tour="custom-table"], table',
              side: 'top',
              align: 'center',
              badge: 'Step 8: API Integration'
            },
            {
              id: 'set-notifications',
              title: 'Notification Settings & Multi-Channel Alerts',
              description: 'Configure automated notification matrices across Email, SMS, Push, and In-App channels for exam registrations, reminder alerts, and test score broadcasts.',
              route: '/settings/notification-settings',
              element: '[data-tour="notification-settings-grid"], .container',
              side: 'top',
              align: 'center',
              badge: 'Step 9: Notifications'
            }
          ]
    },
    {
      id: 'accounts-leads',
      title: 'Accounts & Leads',
      icon: 'user-tie',
      description: 'Pipeline management, lead qualification, corporate account onboarding, and commercial contract schemes.',
      tag: 'CRM & Sales',
      steps: [
        {
          id: 'lead-overview',
          title: 'Leads Pipeline Directory',
          description: 'View the complete prospective customer ledger. Track Lead Name, Industry Vertical, Business Vertical, Acquisition Source, Stage, Primary Contact, and Active Status.',
          route: '/Customer/Leads',
          element: '[data-tour="leads-table"], [data-tour="custom-table"], table',
          side: 'top',
          align: 'center',
          badge: 'Step 1: Leads Directory'
        },
        {
          id: 'lead-search',
          title: 'Leads Quick Search',
          description: 'Instantly search across prospective client organization names, contact persons, email addresses, phone numbers, and cities.',
          route: '/Customer/Leads',
          element: '[data-tour="leads-search"], custom-table .all-text-filter, custom-table .search-wrapper, [data-tour="table-search"]',
          side: 'bottom',
          align: 'end',
          badge: 'Step 2: Search'
        },
        {
          id: 'lead-filters',
          title: 'Leads Multi-Attribute Filter',
          description: 'Filter pipeline leads by Industry Vertical, Lead Source, Creation Date Range, and Status (Active, Converted, Archived).',
          route: '/Customer/Leads',
          element: '[data-tour="leads-filter-btn"], custom-table app-filter, custom-table .filterbtn, [data-tour="table-filter-btn"]',
          side: 'bottom',
          align: 'end',
          badge: 'Step 3: Filter'
        },
        {
          id: 'lead-download',
          title: 'Export Leads Roster',
          description: 'Export the complete or filtered leads database into an Excel spreadsheet (.xlsx) for sales forecasting, pipeline reviews, and offline reporting.',
          route: '/Customer/Leads',
          element: '[data-tour="leads-download-btn"], [data-tour="table-download-btn"], custom-table .inlineDownload, custom-table button:has(.fa-download), custom-table button:has(.fa-file-excel), custom-table button[mattooltip*="Download"], custom-table button[mattooltip*="Export"], custom-table .download',
          side: 'bottom',
          align: 'end',
          badge: 'Step 4: Export'
        },
        {
          id: 'lead-upload',
          title: 'Bulk Leads Import',
          description: 'Import large prospect rosters via Excel/CSV spreadsheet. Download sample templates, validate records automatically, and inspect import error logs.',
          route: '/Customer/Leads',
          element: '[data-tour="leads-upload-btn"], [data-tour="table-upload-btn"], custom-table .upload, custom-table button:has(.fa-upload), custom-table button[mattooltip*="Upload"], custom-table button[mattooltip*="Import"]',
          side: 'bottom',
          align: 'end',
          badge: 'Step 5: Bulk Import'
        },
        {
          id: 'lead-add-btn',
          title: 'Add Lead Action',
          description: 'Click the "+" button in the top right header to open the comprehensive lead registration and qualification form.',
          route: '/Customer/Leads',
          element: '[data-tour="add-lead-btn"], [data-tour="add-question-btn"], custom-table .add, custom-table button:has(.fa-plus), custom-table .action-button',
          side: 'bottom',
          align: 'end',
          badge: 'Step 6: Add Lead'
        },
        {
          id: 'lead-org-fields',
          title: 'Organization & Vertical Fields',
          description: 'Enter Lead Organization Name, select Industry Vertical (e.g., IT, Banking, Healthcare), and assign Business Vertical to classify client domain.',
          route: '/Customer/Leads/AddLead',
          element: 'form, .container',
          side: 'top',
          align: 'center',
          badge: 'Step 7: Organization'
        },
        {
          id: 'lead-geography',
          title: 'Address & Regional Jurisdiction',
          description: 'Specify registered corporate address, State, District, and 6-digit Pincode to map regional sales ownership and operational delivery zones.',
          route: '/Customer/Leads/AddLead',
          element: 'form, .container',
          side: 'top',
          align: 'center',
          badge: 'Step 8: Geography'
        },
        {
          id: 'lead-contact-source',
          title: 'Lead Source & Primary Contacts',
          description: 'Select Lead Source (Inbound Web, Referral, Campaign, Event), enter primary contact Coordinator Name, official Email, Phone Number, and Designation.',
          route: '/Customer/Leads/AddLead',
          element: 'form, .container',
          side: 'top',
          align: 'center',
          badge: 'Step 9: Contacts'
        },
        {
          id: 'lead-save',
          title: 'Save & Index Lead',
          description: 'Verify entered prospect metadata and click Save to store the lead in the active CRM pipeline for sales engagement.',
          route: '/Customer/Leads/AddLead',
          element: 'form, .container',
          side: 'top',
          align: 'center',
          badge: 'Step 10: Save'
        },
        {
          id: 'lead-opp-tracking',
          title: 'Convert to Opportunity',
          description: 'Convert qualified prospects into active deal opportunities. Monitor pipeline deal stages (Discovery, Proposal, Negotiation, Won), win probability, close dates, and projected revenue.',
          route: '/Customer/Leads/Opportunities',
          element: '[data-tour="opportunities-table"], [data-tour="custom-table"], table',
          side: 'top',
          align: 'center',
          badge: 'Step 11: Opportunities'
        },
        {
          id: 'account-overview',
          title: 'Enterprise Accounts Directory',
          description: 'Access the central ledger of contracted enterprise clients, universities, and institutional partners with account manager assignments and active contracts.',
          route: '/Customer/Customer',
          element: '[data-tour="customer-table"], [data-tour="custom-table"], table',
          side: 'top',
          align: 'center',
          badge: 'Step 12: Accounts'
        },
        {
          id: 'account-tools',
          title: 'Account Tools (Search, Export, Import, Filter)',
          description: 'Leverage quick search, Excel data export, CSV bulk customer import, and multi-attribute filters across Industry Verticals and Account States.',
          route: '/Customer/Customer',
          element: 'custom-table .table-actions-container, [data-tour="customer-search"], custom-table .all-text-filter, [data-tour="customer-table"]',
          side: 'top',
          align: 'end',
          badge: 'Step 13: Account Tools'
        },
        {
          id: 'account-add-form',
          title: 'Onboard Enterprise Client Form',
          description: 'Register contracted client accounts with legal corporate entity names, Industry/Business verticals, registered office addresses, State & District, and postal codes.',
          route: '/Customer/Customer/AddCustomer',
          element: 'form, .container',
          side: 'top',
          align: 'center',
          badge: 'Step 14: Onboard Account'
        },
        {
          id: 'account-tax-details',
          title: 'GST, Tax & Commercial Terms',
          description: 'Record GST Number, PAN, Tax exemption identifiers, invoice delivery email, primary billing coordinator, and authorized signatory details.',
          route: '/Customer/Customer/AddCustomer',
          element: 'form, .container',
          side: 'top',
          align: 'center',
          badge: 'Step 15: Tax & Contacts'
        },
        {
          id: 'account-schemes',
          title: 'Custom Schemes & Contracts',
          description: 'Attach customized per-candidate examination pricing schemes, minimum billing quotas, multi-year SLAs, and project delivery contracts.',
          route: '/Customer/Customer/Opportunities',
          element: '[data-tour="custom-table"], table',
          side: 'top',
          align: 'center',
          badge: 'Step 16: Schemes'
        }
      ]
    },
    {
      id: 'bills-invoices',
      title: 'Bills & Invoices',
      icon: 'file-invoice-dollar',
      description: 'Client invoice generation, payment receipts, vendor expense bills, TDS withholding, and master fee cards.',
      tag: 'Finance & Billing',
      steps: [
        {
          id: 'inv-overview',
          title: 'Client Invoices Directory',
          description: 'Monitor issued client invoices, payment status (Paid, Pending, Overdue, Cancelled), due dates, and tax breakdown.',
          route: '/Finance/Invoices',
          element: '[data-tour="invoices-table"], [data-tour="custom-table"], table',
          side: 'top',
          align: 'center',
          badge: 'Step 1: Invoices'
        },
        {
          id: 'inv-search-filter',
          title: 'Invoices Search & Filter',
          description: 'Search invoices by party name or invoice number, apply date-range filters, and filter by payment status (Paid / Pending / Overdue).',
          route: '/Finance/Invoices',
          element: '[data-tour="invoices-search"], custom-table .all-text-filter, custom-table .search-wrapper, [data-tour="table-search"]',
          side: 'bottom',
          align: 'end',
          badge: 'Step 2: Search & Filter'
        },
        {
          id: 'inv-download',
          title: 'Export Invoices & Financial Reports',
          description: 'Download full invoices register, tax summaries (CGST/SGST/IGST), and outstanding receivables into Excel spreadsheets for audits.',
          route: '/Finance/Invoices',
          element: '[data-tour="invoices-download-btn"], [data-tour="cand-download-btn"], [data-tour="table-download-btn"], custom-table button[mattooltip*="Download"], custom-table button[mattooltip*="Export"], custom-table .download',
          side: 'bottom',
          align: 'end',
          badge: 'Step 3: Export Invoices'
        },
        {
          id: 'inv-add-btn',
          title: 'Create New Invoice Button',
          description: 'Click the "+" button in the top right to open the comprehensive invoice generation and billing builder.',
          route: '/Finance/Invoices',
          element: '[data-tour="add-invoice-btn"], [data-tour="add-question-btn"], custom-table .action-button, custom-table .add, custom-table .fa-plus',
          side: 'bottom',
          align: 'end',
          badge: 'Step 4: Create Invoice'
        },
        {
          id: 'inv-party-selection',
          title: 'Invoiced Party & Customer Selection',
          description: 'Select Invoiced To entity (Customer / Organization / Individual), choose target Customer Account, and set Invoice Date and Payment Due Date.',
          route: '/Finance/Invoices/AddInvoice',
          element: 'form, .container',
          side: 'top',
          align: 'center',
          badge: 'Step 5: Bill To Party'
        },
        {
          id: 'inv-line-items',
          title: 'Billable Services & Candidate Counts',
          description: 'Link completed assessment plans, billable candidate counts, unit rates per candidate/hour, add custom service line items, and apply GST tax slabs.',
          route: '/Finance/Invoices/AddInvoice',
          element: 'form, .container',
          side: 'top',
          align: 'center',
          badge: 'Step 6: Line Items & Rates'
        },
        {
          id: 'inv-save-finalize',
          title: 'Save & Generate PDF Invoice',
          description: 'Verify subtotal, tax amounts, grand total, and bank remittance instructions. Click Save to generate official GST-compliant PDF invoices.',
          route: '/Finance/Invoices/AddInvoice',
          element: 'form, .container',
          side: 'top',
          align: 'center',
          badge: 'Step 7: Finalize Invoice'
        },
        {
          id: 'inv-receipts',
          title: 'Payment Receipts & Settlements',
          description: 'Record incoming customer remittances against invoices with transaction reference / UTR numbers, payment modes (NEFT, RTGS, UPI, Cheque), and manage partial settlements.',
          route: '/Finance/Invoices',
          element: '[data-tour="invoices-table"], [data-tour="custom-table"], table',
          side: 'top',
          align: 'center',
          badge: 'Step 8: Record Receipts'
        },
        {
          id: 'bill-overview',
          title: 'Vendor & Expense Bills Directory',
          description: 'Track operational expenses, test center infrastructure rentals, proctor honorariums, question author payouts, and vendor payable balances.',
          route: '/Finance/Bills',
          element: '[data-tour="bills-table"], [data-tour="custom-table"], table',
          side: 'top',
          align: 'center',
          badge: 'Step 9: Expense Bills'
        },
        {
          id: 'bill-tools',
          title: 'Bill Search, Filter & Export',
          description: 'Quick search vendor bills, filter by Vendor Category or Payment State, and export vendor disbursement ledgers to Excel.',
          route: '/Finance/Bills',
          element: '[data-tour="bills-search"], custom-table .all-text-filter, custom-table .search-wrapper, [data-tour="table-search"]',
          side: 'bottom',
          align: 'end',
          badge: 'Step 10: Bill Management'
        },
        {
          id: 'bill-add-form',
          title: 'Record Vendor Bill Form',
          description: 'Select Vendor Category (Proctor, Evaluator, Center Partner, Content Author), pick Vendor Name, and enter Vendor Bill Number and Bill Date.',
          route: '/Finance/Bills/AddBill',
          element: 'form, .container',
          side: 'top',
          align: 'center',
          badge: 'Step 11: Vendor Selection'
        },
        {
          id: 'bill-expense-tax',
          title: 'Expense Amount & TDS Withholding',
          description: 'Allocate bill to specific assessment projects, enter gross expense amount, calculate TDS / withholding tax deductions, and attach vendor invoice PDF.',
          route: '/Finance/Bills/AddBill',
          element: 'form, .container',
          side: 'top',
          align: 'center',
          badge: 'Step 12: Amount & TDS'
        },
        {
          id: 'ratemaster-directory',
          title: 'Rate Master Directory',
          description: 'Centralized standard pricing table displaying Organization, Customer, Service (Assessment/Training/Proctoring), Unit of Measurement (UoM), Unit Rate, Effective Dates, and Active Status.',
          route: '/Finance/RateMaster',
          element: '[data-tour="ratemaster-table"], [data-tour="custom-table"], table',
          side: 'top',
          align: 'center',
          badge: 'Step 13: Rate Master'
        },
        {
          id: 'ratemaster-tools',
          title: 'Rate Master Tools (Search, Export, Import, Filter)',
          description: 'Search rate cards, filter by Customer or Service, export pricing matrices to Excel, and bulk import rate cards via CSV/Excel template upload.',
          route: '/Finance/RateMaster',
          element: '[data-tour="ratemaster-search"], custom-table .all-text-filter, custom-table .search-wrapper, [data-tour="table-search"]',
          side: 'bottom',
          align: 'end',
          badge: 'Step 14: Rate Tools'
        },
        {
          id: 'ratemaster-add',
          title: 'Create & Configure Standard Rate Card',
          description: 'Select Customer, choose Service Type, define Unit of Measurement (Per Candidate, Per Hour, Per Instance, Per Scheme), set Unit Rate currency amount, and define validity date window.',
          route: '/Finance/RateMaster/AddRate',
          element: 'form, .container',
          side: 'top',
          align: 'center',
          badge: 'Step 15: Create Rate Card'
        }
      ]
    }
    ];
  }

  constructor(
    private router: Router,
    private util: UtilService,
    private api: ApiService
  ) {
    // Ensure clean initial state on page refresh - no lingering active step or drawer status
    this.resetTourProgress();

    // Global capture listener: guarantee any click on Skip Tour or close button immediately destroys the tour
    if (typeof document !== 'undefined') {
      const globalCloseHandler = (e: Event) => {
        const target = e.target as HTMLElement;
        if (!target) return;
        if (
          target.closest('.welcome-btn-skip') ||
          target.classList.contains('welcome-btn-skip') ||
          (this.isTourActive && (target.closest('.driver-popover-close-btn') || target.classList.contains('driver-popover-close-btn')))
        ) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.markTourSeenOnServer();
          this.destroyTour();
        }
      };

      document.addEventListener('click', globalCloseHandler, true);
      document.addEventListener('mousedown', globalCloseHandler, true);
      document.addEventListener('pointerdown', globalCloseHandler, true);
    }
  }

  /** Retrieve the unique cache key for the logged in user */
  private getTourUserKey(): string {
    try {
      const email = (localStorage.getItem('useremail') || '').toLowerCase().trim();
      const userId = localStorage.getItem('UserId') || localStorage.getItem('userId') || '';
      return email || userId || 'guest_user';
    } catch (e) {
      return 'guest_user';
    }
  }

  /** Fetch whether current user has seen the tour from database */
  public getTourStatusFromServer(): Observable<any> {
    const rawUserId = localStorage.getItem('UserId') || localStorage.getItem('userId');
    const payload: any = {};
    if (rawUserId) {
      payload.userId = parseInt(rawUserId, 10) || rawUserId;
    }
    console.log('%c[TourService] 📡 Requesting tour status from /user/getTourStatus with payload:', 'color: #0284c7; font-weight: bold;', payload);
    return this.api.post('/user/getTourStatus', { requestBody: payload });
  }

  /** Mark tour as seen in database and local storage */
  public markTourSeenOnServer(): void {
    const userKey = this.getTourUserKey();
    console.log('%c[TourService] 💾 Marking tour as seen for userKey:', 'color: #059669; font-weight: bold;', userKey);
    try {
      localStorage.setItem(`certiplate_tour_seen_${userKey}`, 'true');
      localStorage.setItem('certiplate_tour_seen', 'true');
      sessionStorage.setItem(`certiplate_tour_seen_${userKey}`, 'true');
    } catch (e) {}

    const rawUserId = localStorage.getItem('UserId') || localStorage.getItem('userId');
    const payload: any = { hasSeenTour: true };
    if (rawUserId) {
      payload.userId = parseInt(rawUserId, 10) || rawUserId;
    }

    this.api.post('/user/updateTourStatus', { requestBody: payload }).subscribe({
      next: (res: any) => {
        console.log('%c[TourService] ✅ Successfully updated tour status on server', 'color: #059669;', res);
      },
      error: (err: any) => {
        console.warn('[TourService] ❌ Could not update tour status on server', err);
      }
    });
  }

  /** Auto-start welcome tour based on database status */
  public checkAndAutoStartWelcomeTour(): void {
    const userKey = this.getTourUserKey();
    console.log('%c[TourService] 🔍 checkAndAutoStartWelcomeTour checking server status for userKey:', 'background: #0284c7; color: #fff; font-weight: bold; padding: 2px 6px; border-radius: 3px;', {
      userKey,
      currentUrl: this.router.url
    });

    this.getTourStatusFromServer().subscribe({
      next: (res: any) => {
        const data = res?.body?.responseBody ?? res?.responseBody ?? res?.body ?? res?.result ?? res ?? {};
        const hasSeenTour = data?.hasSeenTour ?? data?.has_seen_tour ?? data?.hasSeen ?? data?.seen ?? res?.body?.hasSeenTour ?? (res?.hasSeenTour);
        console.log('%c[TourService] 📩 Server getTourStatus response:', 'color: #059669; font-weight: bold;', {
          rawResponse: res,
          parsedData: data,
          hasSeenTour
        });
        
        if (hasSeenTour === true || hasSeenTour === 1 || hasSeenTour === '1' || hasSeenTour === 'true') {
          console.log('%c[TourService] ℹ️ User has already seen tour (hasSeenTour=true) in DB. Caching locally.', 'color: #64748b;');
          try {
            localStorage.setItem(`certiplate_tour_seen_${userKey}`, 'true');
            localStorage.setItem('certiplate_tour_seen', 'true');
          } catch (e) {}
          return;
        }

        // hasSeenTour is false, 0, or not yet marked
        console.log('%c[TourService] 🚀 Starting welcome tour because hasSeenTour is false/unseen in DB', 'color: #10b981; font-weight: bold;');
        try {
          localStorage.removeItem(`certiplate_tour_seen_${userKey}`);
          localStorage.removeItem('certiplate_tour_seen');
        } catch (e) {}
        setTimeout(() => {
          window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
          if (document.documentElement) document.documentElement.scrollTop = 0;
          if (document.body) document.body.scrollTop = 0;
          this.launchModuleTour('getting-started');
        }, 400);
      },
      error: (err: any) => {
        console.warn('[TourService] Tour status server check skipped:', err);
      }
    });
  }

  /** Load completed step IDs from localStorage */
  private loadCompletedSteps(): void {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          this._completedStepIds.next(new Set(parsed));
        }
      }
    } catch (e) {
      console.warn('Could not load tour progress from localStorage', e);
    }
  }

  /** Save completed step IDs to localStorage */
  private saveCompletedSteps(ids: Set<string>): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(Array.from(ids)));
      this._completedStepIds.next(new Set(ids));
    } catch (e) {
      console.warn('Could not save tour progress to localStorage', e);
    }
  }

  /** Mark a step as completed */
  public markStepCompleted(stepId: string): void {
    const current = new Set(this._completedStepIds.value);
    current.add(stepId);
    this.saveCompletedSteps(current);
  }

  /** Reset all tour progress and clear lingering status */
  public resetTourProgress(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      sessionStorage.removeItem(this.STORAGE_KEY);
    } catch (e) {}
    this._completedStepIds.next(new Set());
    this._activeTourStep.next(null);
    this._selectedModuleId.next(null);
  }

  /** Open the Guide Drawer Navigation */
  public openGuideDrawer(moduleId?: string): void {
    this.destroyTour();
    if (moduleId) {
      this._selectedModuleId.next(moduleId);
    }
    this._isGuideDrawerOpen.next(true);
  }

  /** Close the Guide Drawer Navigation */
  public closeGuideDrawer(): void {
    this._isGuideDrawerOpen.next(false);
  }

  /** Toggle the Guide Drawer Navigation */
  public toggleGuideDrawer(): void {
    const current = this._isGuideDrawerOpen.value;
    if (current) {
      this.closeGuideDrawer();
    } else {
      this.openGuideDrawer();
    }
  }

  /** Select a specific module in the Guide Drawer */
  public selectModule(moduleId: string | null): void {
    this._selectedModuleId.next(moduleId);
  }

  /** Get completion stats for a module */
  public getModuleProgress(moduleId: string): { completed: number; total: number; percentage: number } {
    const module = this.modules.find(m => m.id === moduleId);
    if (!module || !module.steps.length) {
      return { completed: 0, total: 0, percentage: 0 };
    }
    const completedSet = this._completedStepIds.value;
    const completed = module.steps.filter(s => completedSet.has(s.id)).length;
    const total = module.steps.length;
    const percentage = Math.round((completed / total) * 100);
    return { completed, total, percentage };
  }

  /** Get overall tour progress across all modules */
  public getOverallProgress(): { completed: number; total: number; percentage: number } {
    let total = 0;
    let completed = 0;
    const completedSet = this._completedStepIds.value;
    this.modules.forEach(m => {
      total += m.steps.length;
      completed += m.steps.filter(s => completedSet.has(s.id)).length;
    });
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percentage };
  }

  /**
   * Launch a specific individual tour step:
   * 1. Closes Guide Drawer
   * 2. Navigates automatically to the target page/route
   * 3. Highlights the target element with Driver.js popover
   * 4. Next/Back moves through the module's sequence
   */
  public launchStep(moduleId: string, stepId: string): void {
    const module = this.modules.find(m => m.id === moduleId);
    if (!module) return;
    const step = module.steps.find(s => s.id === stepId);
    if (!step) return;

    this.closeGuideDrawer();
    this.destroyTour();
    const stepIndex = module.steps.indexOf(step);
    this.launchModuleStepIndex(moduleId, stepIndex);
  }

  /** Launch sequential tour for an entire module from start */
  public launchModuleTour(moduleId: string): void {
    const module = this.modules.find(m => m.id === moduleId);
    if (!module || !module.steps.length) return;

    this.closeGuideDrawer();
    this.destroyTour();
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
    this.launchModuleStepIndex(moduleId, 0);
  }

  private isSameRoute(currentUrl: string, targetRoute?: string): boolean {
    if (!targetRoute) return true;
    const cleanCurrent = (currentUrl || '').split('?')[0].split('#')[0].replace(/\/$/, '').toLowerCase();
    const cleanTarget = (targetRoute || '').split('?')[0].split('#')[0].replace(/\/$/, '').toLowerCase();
    return cleanCurrent === cleanTarget;
  }

  /** Helper to determine if a step belongs to the Assessment Rules phase */
  private isAssessmentRulesPhase(stepId: string): boolean {
    return [
      'asm-rules-section', 'asm-rules-stakeholders', 'asm-rules-stakeholders-select',
      'asm-rules-languages-specifier', 'asm-rules-languages-select', 'asm-rules-setscount',
      'asm-rules-primary', 'asm-rules-loginmode',
      'asm-rules-notification', 'asm-rules-deliverymode',
      'asm-rules-deliverycontrols', 'asm-rules-displayrules',
      'asm-rules-questiondisplay', 'asm-rules-monitoring',
      'asm-rules-proctoring', 'asm-rules-evidence', 'asm-save-rules'
    ].includes(stepId);
  }

  /** Ensure the target tab/stepper phase or modal is active before highlighting step elements */
  private preparePageForStep(step: TourStepItem): void {
    if (!step) return;

    // 1. Assessment Plan Stepper: Switch between Assessment (0) and Assessment Rules (1)
    if (step.route && step.route.includes('/Assessments/AssessmentPlan/AddAssessmentPlan')) {
      const isAssessmentPhase = [
        'asm-course', 'asm-version', 'asm-title', 'asm-project',
        'asm-description', 'asm-location', 'asm-start-time', 'asm-end-time',
        'asm-instructions', 'asm-tags', 'asm-custom-attr', 'asm-save-step1'
      ].includes(step.id);

      const isRulesPhase = this.isAssessmentRulesPhase(step.id);

      if (isAssessmentPhase) {
        localStorage.setItem('selectedIndex', '0');
        const headers = document.querySelectorAll('.mat-step-header');
        if (headers.length > 0) {
          (headers[0] as HTMLElement).click();
        }
      } else if (isRulesPhase) {
        localStorage.setItem('selectedIndex', '1');
        const headers = document.querySelectorAll('.mat-step-header');
        if (headers.length > 1) {
          (headers[1] as HTMLElement).click();
        }
      }
    }

    // 1b. Add Question Page Auto-Scroll & Visibility Preparation
    if (step.route && step.route.includes('/Library/Questions/AddQuestion')) {
      const optionEl = document.querySelector('.option') as HTMLElement;
      if (optionEl) {
        optionEl.style.display = 'block';
      }
      const tfEl = document.querySelector('.trueFalseOptions') as HTMLElement;
      if (tfEl) {
        tfEl.style.display = 'block';
      }
      if (['qb-attachments', 'qb-options', 'qb-explanation', 'qb-save'].includes(step.id)) {
        setTimeout(() => {
          let target: HTMLElement | null = null;
          if (step.id === 'qb-save') {
            target = document.querySelector('button#submit.submitbtn, button#submit, [data-tour="q-save-btn"]') as HTMLElement;
          } else if (step.id === 'qb-options') {
            target = document.querySelector('.option, [data-tour="q-options-container"], .trueFalseOptions') as HTMLElement;
          } else if (step.id === 'qb-explanation') {
            target = document.querySelector('[data-tour="q-explanation-field"], input[placeholder*="Answer Explanation"]') as HTMLElement;
          } else if (step.element) {
            target = document.querySelector(step.element) as HTMLElement;
          }
          if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 50);
      }
    }

    // 2. Question Paper Stepper: Switch between Question Paper (0) and Evaluation Criteria (1)
    if (step.route && (step.route.includes('/Library/QuestionPapers/AddQuestionPapers') || step.route.includes('/Library/QuestionPapers/ModifyQuestionPapers'))) {
      const isEvalStep = [
        'qp-eval-title', 'qp-eval-tag-type', 'qp-eval-description',
        'qp-eval-criteria-select', 'qp-eval-params-table',
        'qp-eval-grading-table', 'qp-save-evaluation-btn'
      ].includes(step.id);

      const stepTabs = document.querySelectorAll('.stepper-container .step');
      if (stepTabs.length > 1) {
        if (isEvalStep) {
          (stepTabs[1] as HTMLElement).click();
        } else {
          (stepTabs[0] as HTMLElement).click();
        }
      } else if (stepTabs.length > 0 && !isEvalStep) {
        (stepTabs[0] as HTMLElement).click();
      }

      if (['qp-eval-params-table', 'qp-eval-grading-table', 'qp-save-evaluation-btn'].includes(step.id)) {
        setTimeout(() => {
          let target: HTMLElement | null = null;
          if (step.id === 'qp-save-evaluation-btn') {
            target = document.querySelector('button#submit.submitbtn, [data-tour="qp-save-evaluation-btn"]') as HTMLElement;
          } else if (step.element) {
            target = document.querySelector(step.element) as HTMLElement;
          }
          if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 60);
      }
    }

    // 3. Candidate Modals on Assessment Plan list
    if (step.route && step.route.includes('/Assessments/AssessmentPlan')) {
      const jq = (window as any).$;
      if (jq && typeof jq === 'function') {
        if (step.id === 'asm-action-candidates') {
          jq('#candidateManagementModal')?.modal?.('hide');
          jq('#importCandidate')?.modal?.('hide');
          jq('#addCandidateId')?.modal?.('hide');
          jq('#publishWarningModal')?.modal?.('hide');
        } else if (step.id === 'asm-manage-candidates') {
          jq('#importCandidate')?.modal?.('hide');
          jq('#addCandidateId')?.modal?.('hide');
          jq('#publishWarningModal')?.modal?.('hide');
          jq('#candidateManagementModal')?.modal?.('show');
        } else if (step.id === 'asm-import-candidates') {
          jq('#candidateManagementModal')?.modal?.('hide');
          jq('#addCandidateId')?.modal?.('hide');
          jq('#publishWarningModal')?.modal?.('hide');
          jq('#importCandidate')?.modal?.('show');
        } else if (step.id === 'asm-add-candidate') {
          jq('#candidateManagementModal')?.modal?.('hide');
          jq('#importCandidate')?.modal?.('hide');
          jq('#publishWarningModal')?.modal?.('hide');
          jq('#addCandidateId')?.modal?.('show');
        } else if (step.id === 'asm-publish-btn') {
          jq('#importCandidate')?.modal?.('hide');
          jq('#addCandidateId')?.modal?.('hide');
          jq('#publishWarningModal')?.modal?.('hide');
          jq('#candidateManagementModal')?.modal?.('show');
        } else if (step.id === 'asm-confirm-publish') {
          jq('#candidateManagementModal')?.modal?.('hide');
          jq('#importCandidate')?.modal?.('hide');
          jq('#addCandidateId')?.modal?.('hide');
          jq('#publishWarningModal')?.modal?.('show');
        }
      }
    }

    // 4. Question Paper Modal Management on Question Papers list
    if (step.route && step.route.includes('/Library/QuestionPapers')) {
      const jq = (window as any).$;
      if (jq && typeof jq === 'function') {
        if (step.id === 'qp-generation-methods') {
          jq('#ViewAssessmentDetailPopup')?.modal?.('show');
        } else {
          jq('#ViewAssessmentDetailPopup')?.modal?.('hide');
        }
      }
    }

    // 4b. Subscription Plans Modal Management on Subscriptions list
    if (step.route && step.route.includes('/admin/subscriptions')) {
      const jq = (window as any).$;
      if (step.id === 'set-sub-add-edit') {
        setTimeout(() => {
          const modalEl = document.querySelector('#configureFeaturesModal');
          const isModalOpen = modalEl && modalEl.classList.contains('show');
          if (!isModalOpen) {
            const threeDotsBtn = document.querySelector('custom-table td button[aria-label="menu icon"]') as HTMLElement;
            if (threeDotsBtn) {
              threeDotsBtn.click();
              setTimeout(() => {
                const configBtn = document.querySelector('.action-grid-item.configureFeatures, button.configureFeatures') as HTMLElement;
                if (configBtn) {
                  configBtn.click();
                } else if (jq && typeof jq === 'function') {
                  jq('#configureFeaturesModal')?.modal?.('show');
                }
              }, 250);
            } else if (jq && typeof jq === 'function') {
              jq('#configureFeaturesModal')?.modal?.('show');
            }
          }
        }, 200);
      } else {
        if (jq && typeof jq === 'function') {
          jq('#configureFeaturesModal')?.modal?.('hide');
          jq('#AddorEditPlanModal')?.modal?.('hide');
        }
      }
    }

    // 5. Settings Dropdown Management
    const isSettingsDropdownStep = [
      'set-trigger', 'set-org-settings-menu', 'set-subscriptions-menu',
      'set-dev-console-menu', 'set-user-settings-menu', 'set-integration-menu',
      'set-compliance-menu'
    ].includes(step.id);

    if (isSettingsDropdownStep) {
      const dropdown = document.querySelector('#settingsDropdown');
      if (dropdown) {
        dropdown.classList.add('showDropDown');
      }
      const userSettingsItem = document.querySelector('[data-tour="set-user-settings"]');
      const integrationItem = document.querySelector('[data-tour="set-integration"]');

      if (step.id === 'set-user-settings-menu') {
        userSettingsItem?.classList.add('tour-active-nested');
        integrationItem?.classList.remove('tour-active-nested');
      } else if (step.id === 'set-integration-menu') {
        integrationItem?.classList.add('tour-active-nested');
        userSettingsItem?.classList.remove('tour-active-nested');
      } else {
        userSettingsItem?.classList.remove('tour-active-nested');
        integrationItem?.classList.remove('tour-active-nested');
      }
    } else {
      const dropdown = document.querySelector('#settingsDropdown');
      if (dropdown) {
        dropdown.classList.remove('showDropDown');
      }
      document.querySelectorAll('.tour-active-nested').forEach(el => el.classList.remove('tour-active-nested'));
    }
  }

  private launchModuleStepIndex(moduleId: string, index: number): void {
    const module = this.modules.find(m => m.id === moduleId);
    if (!module || index < 0 || index >= module.steps.length) {
      this.destroyTour();
      return;
    }

    const currentStep = module.steps[index];
    this.isTourActive = true;
    this._selectedModuleId.next(moduleId);
    this._activeTourStep.next({ moduleId, stepId: currentStep.id });

    // Set initial localStorage state before route navigation if heading into AddAssessmentPlan
    if (currentStep.route && currentStep.route.includes('/Assessments/AssessmentPlan/AddAssessmentPlan')) {
      const isRulesPhase = this.isAssessmentRulesPhase(currentStep.id);
      localStorage.setItem('selectedIndex', isRulesPhase ? '1' : '0');
    }

    const driveSteps: DriveStep[] = module.steps.map((s, idx) => {
      const isLast = idx === module.steps.length - 1;
      const isFirst = idx === 0;
      return {
        element: s.element,
        popover: {
          title: s.title,
          description: s.description,
          side: s.side || 'bottom',
          align: s.align || 'start',
          nextBtnText: isLast ? 'Finish Tour' : (isFirst && !s.element ? 'Start Tour' : 'Next'),
          prevBtnText: isFirst ? 'Skip Tour' : 'Back',
          disableButtons: [],
          showButtons: ['next', 'previous', 'close'],
          onNextClick: () => {
            this.markStepCompleted(s.id);
            if (!isLast) {
              const nextStep = module.steps[idx + 1];
              if (this.driverObj) {
                this.driverObj.destroy();
                this.driverObj = null;
              }
              this.cleanupBackdropsAndModals();
              if (nextStep.route && nextStep.route.includes('/Assessments/AssessmentPlan/AddAssessmentPlan')) {
                const isRulesPhase = this.isAssessmentRulesPhase(nextStep.id);
                localStorage.setItem('selectedIndex', isRulesPhase ? '1' : '0');
              }
              if (nextStep.route && !this.isSameRoute(this.router.url, nextStep.route)) {
                this.router.navigateByUrl(nextStep.route).then(() => {
                  this.preparePageForStep(nextStep);
                  this.waitForElement(nextStep.element || 'body').then(() => {
                    setTimeout(() => {
                      this.launchModuleStepIndex(moduleId, idx + 1);
                    }, 300);
                  });
                }).catch(() => {
                  this.launchModuleStepIndex(moduleId, idx + 1);
                });
              } else {
                this.preparePageForStep(nextStep);
                setTimeout(() => {
                  this.launchModuleStepIndex(moduleId, idx + 1);
                }, 100);
              }
            } else {
              this.markTourSeenOnServer();
              this.destroyTour();
              this.handlePostTourNavigation(moduleId);
            }
          },
          onPrevClick: () => {
            if (idx > 0) {
              const prevStep = module.steps[idx - 1];
              if (this.driverObj) {
                this.driverObj.destroy();
                this.driverObj = null;
              }
              this.cleanupBackdropsAndModals();
              if (prevStep.route && prevStep.route.includes('/Assessments/AssessmentPlan/AddAssessmentPlan')) {
                const isRulesPhase = this.isAssessmentRulesPhase(prevStep.id);
                localStorage.setItem('selectedIndex', isRulesPhase ? '1' : '0');
              }
              if (prevStep.route && !this.isSameRoute(this.router.url, prevStep.route)) {
                this.router.navigateByUrl(prevStep.route).then(() => {
                  this.preparePageForStep(prevStep);
                  this.waitForElement(prevStep.element || 'body').then(() => {
                    setTimeout(() => {
                      this.launchModuleStepIndex(moduleId, idx - 1);
                    }, 300);
                  });
                }).catch(() => {
                  this.launchModuleStepIndex(moduleId, idx - 1);
                });
              } else {
                this.preparePageForStep(prevStep);
                setTimeout(() => {
                  this.launchModuleStepIndex(moduleId, idx - 1);
                }, 100);
              }
            } else {
              this.destroyTour();
            }
          }
        }
      };
    });

    if (currentStep.route && !this.isSameRoute(this.router.url, currentStep.route)) {
      if (this.driverObj) {
        this.driverObj.destroy();
        this.driverObj = null;
      }
      this.cleanupBackdropsAndModals();
      this.router.navigateByUrl(currentStep.route).then(() => {
        this.preparePageForStep(currentStep);
        this.waitForElement(currentStep.element || 'body').then(() => {
          setTimeout(() => {
            this.preparePageForStep(currentStep);
            const target = currentStep.element ? document.querySelector(currentStep.element) as HTMLElement : null;
            if (target) {
              const isModal = target.closest('.modal') || target.classList.contains('modal') || target.classList.contains('modal-dialog') || target.classList.contains('modal-content');
              if (!isModal) {
                target.scrollIntoView({ behavior: 'auto', block: 'center' });
              } else {
                window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
                if (document.documentElement) document.documentElement.scrollTop = 0;
                if (document.body) document.body.scrollTop = 0;
              }
            } else {
              window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
              if (document.documentElement) document.documentElement.scrollTop = 0;
              if (document.body) document.body.scrollTop = 0;
            }
            this.driverObj = this.createDriverInstance(driveSteps, driveSteps.length, index);
            this.driverObj.drive(index);
          }, 350);
        });
      }).catch(() => {
        this.driverObj = this.createDriverInstance(driveSteps, driveSteps.length, index);
        this.driverObj.drive(index);
      });
    } else {
      this.preparePageForStep(currentStep);
      this.waitForElement(currentStep.element || 'body').then(() => {
        setTimeout(() => {
          this.preparePageForStep(currentStep);
          const target = currentStep.element ? document.querySelector(currentStep.element) as HTMLElement : null;
          if (target) {
            const isModal = target.closest('.modal') || target.classList.contains('modal') || target.classList.contains('modal-dialog') || target.classList.contains('modal-content');
            if (!isModal) {
              target.scrollIntoView({ behavior: 'auto', block: 'center' });
            } else {
              window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
              if (document.documentElement) document.documentElement.scrollTop = 0;
              if (document.body) document.body.scrollTop = 0;
            }
          } else {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
            if (document.documentElement) document.documentElement.scrollTop = 0;
            if (document.body) document.body.scrollTop = 0;
          }
          this.driverObj = this.createDriverInstance(driveSteps, driveSteps.length, index);
          this.driverObj.drive(index);
        }, 150);
      });
    }
  }

  private createDriveStepFromItem(step: TourStepItem, module: TourModule, currentNum: number, total: number): DriveStep {
    const isWelcome = step.id.includes('welcome') || !step.element;
    return {
      element: step.element,
      popover: {
        title: step.title,
        description: step.description,
        side: step.side || 'bottom',
        align: step.align || 'start',
        nextBtnText: isWelcome ? 'Start Tour' : 'Got it!',
        prevBtnText: isWelcome ? 'Skip Tour' : 'Open Guide',
        disableButtons: [],
        showButtons: ['next', 'previous', 'close'],
        onNextClick: () => {
          this.markStepCompleted(step.id);
          this.markTourSeenOnServer();
          this.destroyTour();
          this.handlePostTourNavigation(module.id);
        },
        onPrevClick: () => {
          this.destroyTour();
          if (!isWelcome) {
            this.openGuideDrawer(module.id);
          }
        }
      }
    };
  }

  private runSingleStepTour(step: DriveStep, stepId: string): void {
    this.isTourActive = true;
    this.driverObj = this.createDriverInstance([step], 1, 0);
    this.driverObj.drive(0);
    this.markStepCompleted(stepId);
  }

  /** Full 30-Step Interactive Workflow Steps (Welcome -> Question Creation -> Question Paper -> Completion) */
  private get workflowSteps(): DriveStep[] {
    return [
      {
        popover: {
          title: "Let's take a quick tour!",
          description: 'Discover how Certiplate helps you create, manage and conduct secure assessments — all in one place.',
          align: 'center',
          nextBtnText: 'Start Tour',
          prevBtnText: 'Skip Tour',
          disableButtons: [],
          showButtons: ['next', 'previous', 'close'],
          onNextClick: () => {
            this.startStep(1);
          },
          onPrevClick: () => {
            this.destroyTour();
          },
        },
      },
      {
        element: '[data-tour="sidebar"]',
        popover: {
          title: 'Question Banks',
          description: 'Start by opening <b>Question Banks</b> from the sidebar. This is where questions are created, organized, and managed.',
          side: 'right',
          align: 'start',
          onNextClick: () => {
            this.navigateToRouteAndContinue('/Library/Questions', 2);
          },
        },
      },
      {
        element: '[data-tour="questions-list-table"], custom-table, .table-wrapper',
        popover: {
          title: 'Question Library',
          description: 'This is the <b>Question Library</b>. You can view, search, filter, and organize all questions available in Certiplate from here.',
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '[data-tour="add-question-btn"]',
        popover: {
          title: 'Add Question',
          description: 'Click the <b>+ Add Question</b> button in the top right to create a new assessment question.',
          side: 'bottom',
          align: 'end',
          onNextClick: () => {
            this.navigateToRouteAndContinue('/Library/Questions/AddQuestion', 4);
          },
        },
      },
      {
        element: '[data-tour="q-course-select"]',
        popover: {
          title: 'Course / Job Role',
          description: 'Select the <b>Course or Job Role</b> for this question (e.g. React JS, Python, or Java).',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="q-version-select"]',
        popover: {
          title: 'Course Version',
          description: 'Select the appropriate <b>course version</b> or syllabus version for this question.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="q-labels-select"]',
        popover: {
          title: 'Organizational Labels',
          description: 'Select the <b>organizational labels</b> used to categorize and index this question.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="q-tags-select"]',
        popover: {
          title: 'Tags & Metadata',
          description: 'Add relevant <b>tags or keywords</b> to help organize and quickly search this question later.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="q-evaluation-mode-select"]',
        popover: {
          title: 'Evaluation Mode',
          description: 'Choose the <b>Evaluation Mode</b> for this question: Theory, Practical, or Viva.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="q-type-select"]',
        popover: {
          title: 'Question Type',
          description: 'Choose the <b>Question Type</b>, such as MCQ, True/False, Fill in the Blanks, or Subjective.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="q-difficulty-select"]',
        popover: {
          title: 'Difficulty Level',
          description: 'Set the <b>Difficulty Level</b> for the question: Easy, Medium, or Hard.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="q-text-editor"]',
        popover: {
          title: 'Question Text Prompt',
          description: 'Enter the <b>question prompt</b> here using the rich text editor. Support formulas, formatting, and images.',
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '[data-tour="q-options-container"], .trueFalseOptions, .rdioLabelDiv, mat-radio-group[formControlName="isCorrectAnswer"], app-addoptiondata, .option',
        popover: {
          title: 'Options & Correct Answer',
          description: 'Enter the available <b>answer choices</b> and mark the correct option as the answer key.',
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '[data-tour="q-save-btn"]',
        popover: {
          title: 'Save Question',
          description: 'Review the question details and click <b>Save</b> to store the question in your Question Bank.',
          side: 'top',
          align: 'center',
          onNextClick: () => {
            this.navigateToRouteAndContinue('/Library/QuestionPapers', 14);
          },
        },
      },
      {
        element: '[data-tour="qp-list-table"]',
        popover: {
          title: 'Question Papers Library',
          description: 'This is the <b>Question Papers Library</b>. From here you can create, blueprint, and generate Question Papers.',
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '[data-tour="add-question-paper-btn"]',
        popover: {
          title: 'Add Question Paper',
          description: 'Click the <b>+ Add Question Paper</b> button to start generating a new examination paper.',
          side: 'bottom',
          align: 'end',
          onNextClick: () => {
            this.navigateToRouteAndContinue('/Library/QuestionPapers/AddQuestionPapers', 16);
          },
        },
      },
      {
        element: '[data-tour="qp-generation-stepper"]',
        popover: {
          title: 'Question Paper Stepper',
          description: 'Question Paper creation is organized into step-by-step phases to configure blueprint rules and generate papers.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '[data-tour="qp-course-select"]',
        popover: {
          title: 'Course / Job Role',
          description: 'Select the target <b>Course / Job Role</b> for this Question Paper.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="qp-labels-select"]',
        popover: {
          title: 'Paper Labels',
          description: 'Select organizational labels to associate and categorize this Question Paper.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="qp-name-input"]',
        popover: {
          title: 'Question Paper Name',
          description: 'Enter a clear and meaningful <b>Question Paper Name</b> for identification.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="qp-shortname-input"]',
        popover: {
          title: 'Paper Short Name',
          description: 'Enter a concise <b>short name or code</b> for tracking.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="qp-time-limit"]',
        popover: {
          title: 'Examination Time Limit',
          description: 'Set the maximum <b>duration in minutes</b> for candidates taking this exam.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="qp-question-count"]',
        popover: {
          title: 'Number of Questions',
          description: 'Specify the <b>total question count</b> to be included in each test paper.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="qp-total-marks"]',
        popover: {
          title: 'Total Marks',
          description: 'Enter the <b>total marks</b> allocated across all questions in the paper.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="qp-version-count"]',
        popover: {
          title: 'Number of Versions',
          description: 'Specify how many <b>unique randomized sets/versions</b> (Set A, Set B, etc.) to generate.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="qp-overlap-slider"]',
        popover: {
          title: 'Question Overlap Allowed',
          description: 'Configure whether questions can <b>overlap between different sets/versions</b> of the paper.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="qp-instructions-editor"]',
        popover: {
          title: 'Candidate Instructions',
          description: 'Enter the guidelines and <b>instructions</b> that candidates will see prior to starting the exam.',
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '[data-tour="qp-generation-stepper"]',
        popover: {
          title: 'Blueprint & Question Selection',
          description: 'Select questions directly from your library or configure automated blueprint selection rules by difficulty.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '[data-tour="qp-generation-stepper"]',
        popover: {
          title: 'Save & Generate Question Paper',
          description: 'Review your Question Paper parameters and click <b>Save & Generate</b> to compile your paper sets.',
          side: 'top',
          align: 'center',
          onNextClick: () => {
            this.startStep(29);
          },
        },
      },
      {
        popover: {
          title: "You're all set!",
          description: 'You’ve explored the complete Question → Question Paper → Generation workflow in Certiplate. You can relaunch this tour anytime from the top bar.',
          align: 'center',
          nextBtnText: 'Finish Tour',
          showButtons: ['next'],
          onNextClick: () => {
            this.destroyTour();
            this.router.navigateByUrl('/Library/QuestionPapers');
          },
        },
      },
    ];
  }

  /** Start tour from Welcome Screen (Step 0) */
  startFullWorkflowTour(): void {
    this.markTourSeenOnServer();
    this.startStep(0);
  }

  /** Quick entry point alias -> Opens the Guide Drawer */
  startDashboardTour(): void {
    this.openGuideDrawer();
  }

  /** Get step icon SVG based on step title or element selector */
  private getStepIconSvg(title: string, selector?: string): string {
    const t = (title || '').toLowerCase();
    const s = (selector || '').toLowerCase();

    if (t.includes('sign in') || t.includes('account') || t.includes('role') || s.includes('login-card')) {
      return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>`;
    }
    if (t.includes('method') || t.includes('switch') || s.includes('segmented')) {
      return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="4" y1="21" x2="4" y2="14"></line>
        <line x1="4" y1="10" x2="4" y2="3"></line>
        <line x1="12" y1="21" x2="12" y2="12"></line>
        <line x1="12" y1="8" x2="12" y2="3"></line>
        <line x1="20" y1="21" x2="20" y2="16"></line>
        <line x1="20" y1="12" x2="20" y2="3"></line>
        <line x1="1" y1="14" x2="7" y2="14"></line>
        <line x1="9" y1="8" x2="15" y2="8"></line>
        <line x1="17" y1="16" x2="23" y2="16"></line>
      </svg>`;
    }
    if (t.includes('email') || s.includes('email')) {
      return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
        <polyline points="22,6 12,13 2,6"></polyline>
      </svg>`;
    }
    if (t.includes('password') || t.includes('forgot') || s.includes('password')) {
      return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
      </svg>`;
    }
    if (t.includes('submit') || t.includes('sign in') || s.includes('submit')) {
      return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
        <polyline points="10 17 15 12 10 7"></polyline>
        <line x1="15" y1="12" x2="3" y2="12"></line>
      </svg>`;
    }
    if (t.includes('question library') || t.includes('question banks') || t.includes('essential features') || s.includes('sidebar')) {
      return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
      </svg>`;
    }
    if (t.includes('add') || s.includes('add')) {
      return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="16"></line>
        <line x1="8" y1="12" x2="16" y2="12"></line>
      </svg>`;
    }
    if (t.includes('paper') || t.includes('stepper') || t.includes('blueprint')) {
      return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
        <polyline points="2 17 12 22 22 17"></polyline>
        <polyline points="2 12 12 17 22 12"></polyline>
      </svg>`;
    }
    if (t.includes('time') || t.includes('limit') || s.includes('time')) {
      return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
      </svg>`;
    }
    if (t.includes('all set') || t.includes('complete') || t.includes('finish') || t.includes('save')) {
      return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>`;
    }

    return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="12" y1="16" x2="12" y2="12"></line>
      <line x1="12" y1="8" x2="12.01" y2="8"></line>
    </svg>`;
  }

  /** Render Welcome Illustration SVG */
  private getWelcomeIllustrationSvg(): string {
    return `<svg viewBox="0 0 320 270" fill="none" xmlns="http://www.w3.org/2000/svg" class="welcome-illustration-svg">
      <defs>
        <linearGradient id="bgMintGlow" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#f0fdf4" />
          <stop offset="100%" stop-color="#dcfce7" />
        </linearGradient>
        <filter id="softCardShadow" x="-10%" y="-10%" width="130%" height="130%">
          <feDropShadow dx="0" dy="8" stdDeviation="10" flood-color="#059669" flood-opacity="0.12" />
        </filter>
      </defs>

      <rect width="320" height="270" rx="18" fill="url(#bgMintGlow)" />

      <g font-family="'Segoe UI', Roboto, -apple-system, sans-serif" font-weight="700" fill="#047857">
        <text x="75" y="42" font-size="18" transform="rotate(-15 75 42)" opacity="0.95">Explore</text>
        <text x="88" y="70" font-size="17" transform="rotate(-10 88 70)" opacity="0.95">Learn</text>
        <text x="100" y="98" font-size="18" transform="rotate(-8 100 98)" opacity="0.95">Do more</text>
      </g>

      <path d="M 135 102 C 150 120, 165 80, 185 70 C 205 60, 215 85, 235 60" 
            stroke="#10b981" stroke-width="1.8" stroke-dasharray="3 3" fill="none" opacity="0.85" />

      <g transform="translate(230, 38) rotate(15) scale(0.9)">
        <polygon points="0,26 36,0 12,32" fill="#047857" />
        <polygon points="36,0 12,32 20,40" fill="#10b981" />
        <polygon points="0,26 36,0 20,40" fill="#34d399" opacity="0.65" />
      </g>

      <g filter="url(#softCardShadow)">
        <rect x="50" y="100" width="186" height="120" rx="12" fill="#ffffff" stroke="#e2e8f0" stroke-width="1.5" />
        
        <circle cx="64" cy="112" r="3" fill="#10b981" />
        <circle cx="72" cy="112" r="3" fill="#34d399" />
        <circle cx="80" cy="112" r="3" fill="#cbd5e1" />
        
        <rect x="156" y="108" width="66" height="8" rx="4" fill="#f1f5f9" />
        <circle cx="216" cy="112" r="2" fill="#10b981" />

        <text x="62" y="134" font-family="'Inter', -apple-system, sans-serif" font-size="13" font-weight="700" fill="#047857">Certiplate</text>
        <path d="M 124 129 L 126 124 L 128 129 Z" fill="#059669" />

        <g transform="translate(62, 148)">
          <circle cx="5" cy="5" r="5" fill="#dcfce7" />
          <path d="M 3.5 5.5 L 4.5 6.5 L 6.5 4" stroke="#059669" stroke-width="1.2" stroke-linecap="round" fill="none" />
          <rect x="16" y="2" width="102" height="6" rx="3" fill="#e2e8f0" />
        </g>
        <g transform="translate(62, 166)">
          <circle cx="5" cy="5" r="5" fill="#dcfce7" />
          <path d="M 4 4.5 L 4 3.5 C 4 2.8 4.5 2.2 5 2.2 C 5.5 2.2 6 2.8 6 3.5 L 6 4.5 M 3 4.5 L 7 4.5 L 7 7.5 L 3 7.5 Z" stroke="#059669" stroke-width="1" fill="none" />
          <rect x="16" y="2" width="76" height="6" rx="3" fill="#e2e8f0" />
        </g>
        <g transform="translate(62, 184)">
          <circle cx="5" cy="5" r="5" fill="#dcfce7" />
          <path d="M 4 4.5 L 4 3.5 C 4 2.8 4.5 2.2 5 2.2 C 5.5 2.2 6 2.8 6 3.5 L 6 4.5 M 3 4.5 L 7 4.5 L 7 7.5 L 3 7.5 Z" stroke="#059669" stroke-width="1" fill="none" />
          <rect x="16" y="2" width="88" height="6" rx="3" fill="#e2e8f0" />
        </g>
      </g>

      <g transform="translate(216, 182)">
        <polygon points="12,50 30,50 27,68 15,68" fill="#047857" />
        <rect x="10" y="47" width="22" height="4" rx="2" fill="#065f46" />
        <path d="M 21 47 Q 10 28 18 14 Q 26 28 21 47 Z" fill="#10b981" />
        <path d="M 21 47 Q 34 30 30 16 Q 22 30 21 47 Z" fill="#059669" />
        <path d="M 21 47 Q 6 36 4 22 Q 14 32 21 47 Z" fill="#34d399" />
        <path d="M 21 47 Q 36 40 38 26 Q 28 34 21 47 Z" fill="#10b981" />
      </g>
    </svg>`;
  }

  /** Common Driver instance builder ensuring single active popup in DOM */
  private createDriverInstance(steps: DriveStep[], totalSteps: number, initialStepIndex: number = 0): Driver {
    // Purge any existing popover elements in the DOM before creating new instance
    document.querySelectorAll('.driver-popover, .driver-overlay, .driver-stage').forEach(el => el.remove());

    return driver({
      showProgress: false,
      animate: true,
      smoothScroll: true,
      overlayColor: '#0f172a',
      overlayOpacity: 0.45,
      stagePadding: 8,
      stageRadius: 14,
      allowClose: true,
      popoverClass: 'certiplate-tour-popover',
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Finish Tour',
      steps: steps,
      onPopoverRender: (popoverDOM: any, { config, state }: any) => {
        const stepIndex = state?.activeIndex ?? 0;
        const currentStepNum = stepIndex + 1;
        const total = config?.steps?.length || totalSteps;
        const step = config?.steps?.[stepIndex];
        const isWelcome = stepIndex === 0 && !step?.element;
        const isLastStep = currentStepNum === total;

        console.log('%c[TourService] 🎨 onPopoverRender fired!', 'color: #d97706; font-weight: bold;', {
          stepIndex,
          currentStepNum,
          total,
          isWelcome,
          wrapper: popoverDOM?.wrapper
        });

        if (!popoverDOM || !popoverDOM.wrapper) return;

        // CASE 1: WELCOME SCREEN
        if (isWelcome) {
          window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
          if (document.documentElement) document.documentElement.scrollTop = 0;
          if (document.body) document.body.scrollTop = 0;

          popoverDOM.wrapper.classList.add('certiplate-tour-welcome-popover');
          popoverDOM.wrapper.classList.remove('certiplate-tour-step-popover');

          if (popoverDOM.title) {
            popoverDOM.title.innerHTML = `
              <div class="welcome-kicker-tag">WELCOME TO CERTIPLATE</div>
              <div class="welcome-main-title">Let's take a quick tour!</div>
            `;
          }

          if (popoverDOM.description) {
            popoverDOM.description.innerHTML = `
              <div class="welcome-modal-grid">
                <div class="welcome-col-left">
                  <p class="welcome-main-desc">Discover how Certiplate helps you create, manage and conduct secure assessments — all in one place.</p>
                  
                  <div class="welcome-features-stack">
                    <div class="welcome-feature-item">
                      <div class="welcome-feature-icon-circle">
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                        </svg>
                      </div>
                      <div class="welcome-feature-text">
                        <div class="welcome-feature-title">Learn essential features</div>
                        <div class="welcome-feature-sub">Get familiar with the key features.</div>
                      </div>
                    </div>

                    <div class="welcome-feature-item">
                      <div class="welcome-feature-icon-circle">
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                        </svg>
                      </div>
                      <div class="welcome-feature-text">
                        <div class="welcome-feature-title">Work faster and smarter</div>
                        <div class="welcome-feature-sub">Save time with guided steps.</div>
                      </div>
                    </div>

                    <div class="welcome-feature-item">
                      <div class="welcome-feature-icon-circle">
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                      </div>
                      <div class="welcome-feature-text">
                        <div class="welcome-feature-title">Designed for your role</div>
                        <div class="welcome-feature-sub">See what matters most to you.</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="welcome-col-right">
                  ${this.getWelcomeIllustrationSvg()}
                </div>
              </div>
            `;
          }

          if (popoverDOM.nextButton) {
            popoverDOM.nextButton.innerHTML = 'Start Tour';
            popoverDOM.nextButton.className = 'driver-popover-next-btn welcome-btn-start';
            popoverDOM.nextButton.style.display = '';
          }

          if (popoverDOM.previousButton) {
            const prevBtn = popoverDOM.previousButton;
            const newPrevBtn = prevBtn.cloneNode(true) as HTMLButtonElement;
            newPrevBtn.innerHTML = 'Skip Tour';
            newPrevBtn.className = 'driver-popover-prev-btn welcome-btn-skip';
            newPrevBtn.removeAttribute('disabled');
            newPrevBtn.disabled = false;
            newPrevBtn.classList.remove('driver-popover-btn-disabled');
            newPrevBtn.style.display = 'inline-flex';
            newPrevBtn.style.pointerEvents = 'auto';
            newPrevBtn.style.cursor = 'pointer';
            newPrevBtn.style.opacity = '1';

            if (prevBtn.parentNode) {
              prevBtn.parentNode.replaceChild(newPrevBtn, prevBtn);
            }

            const handleSkip = (e: Event) => {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              this.markTourSeenOnServer();
              this.destroyTour();
            };

            newPrevBtn.onclick = handleSkip;
            newPrevBtn.addEventListener('click', handleSkip, true);
            newPrevBtn.addEventListener('mousedown', handleSkip, true);
            newPrevBtn.addEventListener('pointerdown', handleSkip, true);
            newPrevBtn.addEventListener('touchstart', handleSkip, true);
          }

          if (popoverDOM.closeButton) {
            const closeBtn = popoverDOM.closeButton;
            const newCloseBtn = closeBtn.cloneNode(true) as HTMLElement;
            newCloseBtn.className = 'driver-popover-close-btn';
            newCloseBtn.removeAttribute('disabled');
            (newCloseBtn as any).disabled = false;
            newCloseBtn.classList.remove('driver-popover-btn-disabled');
            newCloseBtn.style.display = 'flex';
            newCloseBtn.style.pointerEvents = 'auto';
            newCloseBtn.style.cursor = 'pointer';
            newCloseBtn.style.opacity = '1';

            if (closeBtn.parentNode) {
              closeBtn.parentNode.replaceChild(newCloseBtn, closeBtn);
            }

            const handleClose = (e: Event) => {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              this.markTourSeenOnServer();
              this.destroyTour();
            };

            newCloseBtn.onclick = handleClose;
            newCloseBtn.addEventListener('click', handleClose, true);
            newCloseBtn.addEventListener('mousedown', handleClose, true);
            newCloseBtn.addEventListener('pointerdown', handleClose, true);
            newCloseBtn.addEventListener('touchstart', handleClose, true);
          }

          const welcomePagination = popoverDOM.footer?.querySelector('.welcome-pagination-group');
          if (welcomePagination) {
            welcomePagination.remove();
          }

          const oldSkip = popoverDOM.footer?.querySelector('.step-skip-tour-btn');
          if (oldSkip) oldSkip.remove();

          return;
        }

        // CASE 2: STEP POPOVER
        popoverDOM.wrapper.classList.add('certiplate-tour-step-popover');
        popoverDOM.wrapper.classList.remove('certiplate-tour-welcome-popover');

        const stepTitle = step?.popover?.title || 'Tour Guide';
        const stepDesc = step?.popover?.description || '';
        const iconSvg = this.getStepIconSvg(stepTitle, step?.element as string);
        const progressPercentage = Math.round((currentStepNum / total) * 100);

        if (popoverDOM.title) {
          popoverDOM.title.innerHTML = `
            <div class="step-header-row">
              <div class="step-icon-badge">
                ${iconSvg}
              </div>
              <div class="step-header-text-block">
                <span class="step-badge-label">Step ${currentStepNum} of ${total}</span>
                <h3 class="step-main-title">${stepTitle}</h3>
              </div>
            </div>
          `;
        }

        if (popoverDOM.description) {
          popoverDOM.description.innerHTML = `
            <div class="step-body-content">
              ${stepDesc}
            </div>
            <div class="step-progress-section">
              <div class="step-progress-track">
                <div class="step-progress-bar-fill" style="width: ${progressPercentage}%"></div>
              </div>
              <div class="step-progress-counter-text">${currentStepNum} of ${total}</div>
            </div>
          `;
        }

        const oldWelcomePag = popoverDOM.footer?.querySelector('.welcome-pagination-group');
        if (oldWelcomePag) oldWelcomePag.remove();

        let guideBtn = popoverDOM.footer?.querySelector('.step-open-guide-btn') as HTMLButtonElement | null;
        if (!guideBtn && popoverDOM.footer) {
          guideBtn = document.createElement('button');
          guideBtn.type = 'button';
          guideBtn.className = 'step-skip-tour-btn step-open-guide-btn';
          guideBtn.textContent = 'Guide Menu';
          guideBtn.onclick = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            this.destroyTour();
            this.openGuideDrawer();
          };
          popoverDOM.footer.insertBefore(guideBtn, popoverDOM.footer.firstChild || null);
        }

        if (popoverDOM.previousButton) {
          popoverDOM.previousButton.innerHTML = 'Back';
          popoverDOM.previousButton.className = 'driver-popover-prev-btn step-prev-nav-btn';
          popoverDOM.previousButton.style.display = currentStepNum > 1 ? '' : 'none';
        }

        if (popoverDOM.nextButton) {
          popoverDOM.nextButton.innerHTML = isLastStep ? 'Finish Tour' : 'Next';
          popoverDOM.nextButton.className = 'driver-popover-next-btn step-next-nav-btn';
          popoverDOM.nextButton.style.display = '';
        }
      },
      onDestroyStarted: () => {
        const activeModId = this._selectedModuleId?.value || '';
        this.destroyTour();
        this.handlePostTourNavigation(activeModId);
      },
    });
  }

  /** Programmatic post-tour navigation for modules that end on sub-routes */
  private handlePostTourNavigation(moduleId?: string): void {
    const currentUrl = this.router.url || '';
    if (moduleId === 'question-papers' || currentUrl.includes('/Library/QuestionPapers/QuestionPaperGeneration') || currentUrl.includes('/Library/QuestionPapers/AddQuestionPapers')) {
      if (!this.isSameRoute(currentUrl, '/Library/QuestionPapers')) {
        this.router.navigateByUrl('/Library/QuestionPapers');
      }
    } else if (moduleId === 'accounts-leads' || currentUrl.includes('/Customer/Leads') || currentUrl.includes('/Customer/Customer')) {
      if (!this.isSameRoute(currentUrl, '/Customer/Customer')) {
        this.router.navigateByUrl('/Customer/Customer');
      }
    }
  }

  /** Start tour at a specific step index */
  private startStep(stepIndex: number): void {
    this.destroyTour();
    this.isTourActive = true;
    this.currentStepIndex = stepIndex;

    const steps = this.workflowSteps;
    this.driverObj = this.createDriverInstance(steps, steps.length, stepIndex);
    this.driverObj.drive(stepIndex);
  }

  /** Programmatically navigate routes and resume tour at step index */
  private navigateToRouteAndContinue(targetRoute: string, targetStepIndex: number): void {
    this.destroyTour();

    this.router.navigateByUrl(targetRoute).then(() => {
      const selector = this.workflowSteps[targetStepIndex]?.element as string || 'body';
      this.waitForElement(selector).then(() => {
        setTimeout(() => {
          this.startStep(targetStepIndex);
        }, 300);
      });
    }).catch(() => {
      this.startStep(targetStepIndex);
    });
  }

  /** Wait until DOM element renders before focusing */
  private waitForElement(selector: string, timeoutMs: number = 3500): Promise<boolean> {
    return new Promise((resolve) => {
      if (!selector || selector === 'body' || document.querySelector(selector)) {
        return resolve(true);
      }

      const startTime = Date.now();
      const timer = setInterval(() => {
        if (document.querySelector(selector)) {
          clearInterval(timer);
          resolve(true);
        } else if (Date.now() - startTime > timeoutMs) {
          clearInterval(timer);
          resolve(false);
        }
      }, 100);
    });
  }

  /** Clean up any open bootstrap modals, backdrops, and body locks */
  private cleanupBackdropsAndModals(): void {
    const jq = (window as any).$;
    if (jq && typeof jq === 'function') {
      try {
        jq('#candidateManagementModal')?.modal?.('hide');
        jq('#importCandidate')?.modal?.('hide');
        jq('#addCandidateId')?.modal?.('hide');
        jq('#publishWarningModal')?.modal?.('hide');
        jq('#ViewAssessmentDetailPopup')?.modal?.('hide');
        jq('#manageLicenseModal')?.modal?.('hide');
        jq('#AddorEditPlanModal')?.modal?.('hide');
        jq('#configureFeaturesModal')?.modal?.('hide');
        jq('#configureFeaturesModal2')?.modal?.('hide');
        jq('.modal')?.modal?.('hide');
      } catch (e) {}
    }
    // Purge any lingering Bootstrap backdrops
    document.querySelectorAll('.modal-backdrop').forEach(el => {
      try {
        el.remove();
      } catch (e) {}
    });
    document.body.classList.remove('modal-open', 'driver-active', 'driver-no-interaction', 'driver-active-element');
    document.documentElement.classList.remove('driver-active', 'driver-no-interaction', 'driver-active-element');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
    document.body.style.removeProperty('pointer-events');
    if (document.documentElement) {
      document.documentElement.style.removeProperty('pointer-events');
    }
  }

  /** Destroy tour cleanly and purge DOM elements */
  destroyTour(): void {
    this.isTourActive = false;
    this._activeTourStep.next(null);
    this._isGuideDrawerOpen.next(false);
    this.resetTourProgress();

    if (this.driverObj) {
      const d = this.driverObj;
      this.driverObj = null;
      try {
        d.destroy();
      } catch (e) {}
    }

    this.cleanupBackdropsAndModals();

    const dropdown = document.querySelector('#settingsDropdown');
    if (dropdown) {
      dropdown.classList.remove('showDropDown');
    }
    document.querySelectorAll('.tour-active-nested').forEach(el => el.classList.remove('tour-active-nested'));
    // Remove any leftover driver.js popover, stage, overlay, SVG overlay or active elements from DOM
    document.querySelectorAll('.driver-popover, .driver-overlay, .driver-stage, svg.driver-overlay, .driver-active-element').forEach(el => {
      try {
        el.remove();
      } catch (e) {}
    });
    document.body.classList.remove('driver-active', 'driver-no-interaction', 'driver-active-element', 'modal-open');
    document.documentElement.classList.remove('driver-active', 'driver-no-interaction', 'driver-active-element');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
    document.body.style.removeProperty('pointer-events');
    if (document.documentElement) {
      document.documentElement.style.removeProperty('pointer-events');
    }
  }
}
